import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import {
  whoami, logout, listTeamMembers, getDemoPersonas, demoSignIn, getAccountDetails,
  switchWorkspace as switchWorkspaceOp,
  inviteTeamMember, updateTeamMemberRole, resendTeamInvite, removeTeamMember as removeTeamMemberOp,
  setStudioSessionActive,
} from "@v0y4ge/api-client";
import { refreshTranslationCatalog, t as i18nT } from "../lib/i18n";
import { toast } from "./toast";

/**
 * Capability model — the stable surface the route guard + every screen's action
 * gates call: `can(perm)`, `useUser()`.
 *
 * This revision implements the role architecture from "Event Roles & Access":
 *  • Five Events roles (Event Creator / Coordinator / Manager / Approver / System Administrator).
 *  • Capabilities are the contract — features check `can("area.verb")`, never a role name.
 *  • Roles are PROCESS-SCOPED (events is built; queues / appointments come later and get
 *    their own role sets). A role belongs to a process; a user is assigned a role per process.
 *  • Role default capability sets are EDITABLE at runtime by an admin (persisted) — the
 *    Team & roles screen writes them.
 *  • Per-user access is ADDITIVE on top of the role: effective = roleDefaults ∪ grants − revokes.
 *    This is what lets an admin give one Coordinator `guests.import` without cloning the role.
 *
 * Data-layer note (for Claude Code): role edits, per-user grant/revoke, and the member list
 * persist to localStorage here so the design renders + demos statelessly. Wiring these to the
 * backend (saveRole, saveMemberAccess, listTeamMembers) is the data-layer's job; the UI already
 * calls the functions below.
 */

/* ─── Processes (role domains) ─────────────────────────────────────────
 * Events is built. Workforce is built (2026-07-16 scaffold — spec:
 * specs/workforce-management-spec-2026-07-16.md). Queues + Appointments are
 * placeholders so the admin UI can show the dimension and the model is ready
 * when those surfaces land. */
export type ProcessId = "events" | "queues" | "appointments" | "workforce" | "storeops";
interface LocalizedLabel { readonly label: string; labelKey: string; }
export interface ProcessDef extends LocalizedLabel { id: ProcessId; built: boolean; blurb: string; }
export const PROCESSES: ProcessDef[] = [
  { id: "events",       labelKey: "shell.access.events", get label() { return i18nT("shell.access.events", "Events"); },       built: true,  blurb: "Create, approve, run and report on events" },
  { id: "queues",       labelKey: "shell.access.queues", get label() { return i18nT("shell.access.queues", "Queues"); },       built: true,  blurb: "Walk-in queues: joining, serving, messaging, health and regional insight" },
  { id: "workforce",    labelKey: "shell.access.workforce", get label() { return i18nT("shell.access.workforce", "Workforce"); },    built: true,  blurb: "Schedules, demand and day-of staffing across events, appointments and queues" },
  { id: "storeops",     labelKey: "shell.access.storeOps", get label() { return i18nT("shell.access.storeOps", "Store Ops"); },    built: true,  blurb: "Luxury-retail store operations — floor, clienteling, rosters and targets" },
  { id: "appointments", labelKey: "shell.access.appointments", get label() { return i18nT("shell.access.appointments", "Appointments"); }, built: true, blurb: "1:1 and group appointments — booking, day book, comms and reporting" },
];

/* ─── Capability catalog (grouped, process-scoped) ─────────────────────
 * The labelled, grouped, risk-flagged list the role/permission editor renders.
 * Adding a feature = add a cap here, assign it to roles, gate it with can(). */
export interface Cap extends LocalizedLabel { id: string; risk?: boolean; isNew?: boolean; }
export interface CapGroup extends LocalizedLabel { id: string; process: ProcessId; caps: Cap[]; }

export const PERM_CATALOG: CapGroup[] = [
  { id: "events", labelKey: "shell.access.events", get label() { return i18nT("shell.access.events", "Events"); }, process: "events", caps: [
    { id: "events.read",           labelKey: "shell.access.viewEvents", get label() { return i18nT("shell.access.viewEvents", "View events"); } },
    { id: "events.write",          labelKey: "shell.access.createEditEvents", get label() { return i18nT("shell.access.createEditEvents", "Create & edit events"); } },
    { id: "events.discard_draft",  labelKey: "shell.access.discardOwnDraft", get label() { return i18nT("shell.access.discardOwnDraft", "Discard own draft"); }, isNew: true },
    { id: "events.unpublish",      labelKey: "shell.access.takeALiveEventOffline", get label() { return i18nT("shell.access.takeALiveEventOffline", "Take a live event offline"); }, risk: true, isNew: true },
    { id: "events.approve",        labelKey: "shell.access.approveRejectSubmittedEvents", get label() { return i18nT("shell.access.approveRejectSubmittedEvents", "Approve / reject submitted events"); }, isNew: true },
    { id: "events.delete",         labelKey: "shell.access.cancelDeleteALiveEvent", get label() { return i18nT("shell.access.cancelDeleteALiveEvent", "Cancel / delete a live event"); }, risk: true },
  ]},
  { id: "guests", labelKey: "shell.access.guests", get label() { return i18nT("shell.access.guests", "Guests"); }, process: "events", caps: [
    { id: "guests.read",    labelKey: "shell.access.viewGuestListPii", get label() { return i18nT("shell.access.viewGuestListPii", "View guest list & PII"); } },
    { id: "guests.write",   labelKey: "shell.access.addEditSegmentGuests", get label() { return i18nT("shell.access.addEditSegmentGuests", "Add, edit & segment guests"); } },
    { id: "guests.import",  labelKey: "shell.access.bulkImportAGuestListCsv", get label() { return i18nT("shell.access.bulkImportAGuestListCsv", "Bulk-import a guest list (CSV)"); }, risk: true, isNew: true },
    { id: "guests.checkin", labelKey: "shell.access.dayOfCheckIn", get label() { return i18nT("shell.access.dayOfCheckIn", "Day-of check-in"); } },
    /* 2026-10-02: reservation workflow — approve / decline requests on staff-approved tiers. */
    { id: "guests.approve", labelKey: "shell.access.approveReservations", get label() { return i18nT("shell.access.approveReservations", "Approve or decline reservation requests"); }, isNew: true },
  ]},
  { id: "campaigns", labelKey: "shell.access.campaignsComms", get label() { return i18nT("shell.access.campaignsComms", "Campaigns & comms"); }, process: "events", caps: [
    { id: "campaigns.read",            labelKey: "shell.access.viewDraftsHistory", get label() { return i18nT("shell.access.viewDraftsHistory", "View drafts & history"); } },
    { id: "campaigns.write",           labelKey: "shell.access.draftEditComms", get label() { return i18nT("shell.access.draftEditComms", "Draft & edit comms"); } },
    { id: "campaigns.send",            labelKey: "shell.access.sendScheduleToGuests", get label() { return i18nT("shell.access.sendScheduleToGuests", "Send / schedule to guests"); }, risk: true },
    { id: "campaigns.send_individual", labelKey: "shell.access.messageANamedIndividual", get label() { return i18nT("shell.access.messageANamedIndividual", "Message a named individual"); }, risk: true },
    { id: "campaigns.approve",         labelKey: "shell.access.approveRegulatedComms", get label() { return i18nT("shell.access.approveRegulatedComms", "Approve regulated comms"); } },
    /* Email and SMS templates (2026-10-02, operator): shared email layouts and the
     * per-message-type email + SMS templates under Messaging > Templates. In NO role
     * bundle on purpose: System Administrator resolves to the whole catalog, so the
     * screen is System Administrator-only by default. Its own family, not
     * campaigns.* or settings.*: those would hand it to event creators/managers or
     * tenant admins. Grant it to any other role later through the role editor; no
     * code change. The server issues the same two ids (canonical catalog work is
     * tracked with the template API), so a signed-in tenant session sees the tab
     * once the backend grants it. Catalog keys pending: fallbacks carry the English. */
    { id: "messaging.templates.read",  labelKey: "shell.access.viewMessageTemplates", get label() { return i18nT("shell.access.viewMessageTemplates", "View email & SMS templates"); }, isNew: true },
    { id: "messaging.templates.write", labelKey: "shell.access.editMessageTemplates", get label() { return i18nT("shell.access.editMessageTemplates", "Edit & publish email & SMS templates"); }, risk: true, isNew: true },
  ]},
  { id: "flows", labelKey: "shell.access.messagingFlows", get label() { return i18nT("shell.access.messagingFlows", "Messaging flows"); }, process: "events", caps: [
    { id: "flows.read",  labelKey: "shell.access.viewFlows", get label() { return i18nT("shell.access.viewFlows", "View flows"); } },
    { id: "flows.write", labelKey: "shell.access.buildEditFlows", get label() { return i18nT("shell.access.buildEditFlows", "Build & edit flows"); } },
  ]},
  { id: "questions", labelKey: "shell.access.bookingQuestions", get label() { return i18nT("shell.access.bookingQuestions", "Booking questions"); }, process: "events", caps: [
    { id: "questions.read",  labelKey: "shell.access.viewQuestions", get label() { return i18nT("shell.access.viewQuestions", "View questions"); } },
    { id: "questions.write", labelKey: "shell.access.createEditQuestions", get label() { return i18nT("shell.access.createEditQuestions", "Create & edit questions"); } },
  ]},
  { id: "promos", labelKey: "shell.access.couponsPromos", get label() { return i18nT("shell.access.couponsPromos", "Coupons & promos"); }, process: "events", caps: [
    { id: "promos.read",  labelKey: "shell.access.viewCouponsPromos", get label() { return i18nT("shell.access.viewCouponsPromos", "View coupons & promos"); } },
    { id: "promos.write", labelKey: "shell.access.createEditCouponsPromos", get label() { return i18nT("shell.access.createEditCouponsPromos", "Create & edit coupons & promos"); } },
  ]},
  { id: "surveys", labelKey: "shell.access.surveys", get label() { return i18nT("shell.access.surveys", "Surveys"); }, process: "events", caps: [
    { id: "surveys.read",  labelKey: "shell.access.viewSurveysSurveyQuestions", get label() { return i18nT("shell.access.viewSurveysSurveyQuestions", "View surveys & survey questions"); }, isNew: true },
    { id: "surveys.write", labelKey: "shell.access.createEditSurveys", get label() { return i18nT("shell.access.createEditSurveys", "Create & edit surveys"); }, isNew: true },
  ]},
  { id: "services", labelKey: "shell.access.services", get label() { return i18nT("shell.access.services", "Services"); }, process: "events", caps: [
    { id: "services.read",  labelKey: "shell.access.viewServicesCategoriesPackages", get label() { return i18nT("shell.access.viewServicesCategoriesPackages", "View services, categories & packages"); }, isNew: true },
    { id: "services.write", labelKey: "shell.access.createEditServices", get label() { return i18nT("shell.access.createEditServices", "Create & edit services"); }, isNew: true },
  ]},
  { id: "products", labelKey: "shell.access.products", get label() { return i18nT("shell.access.products", "Products"); }, process: "events", caps: [
    { id: "products.read",  labelKey: "shell.access.viewTheProductCatalog", get label() { return i18nT("shell.access.viewTheProductCatalog", "View the product catalog"); }, isNew: true },
    { id: "products.write", labelKey: "shell.access.manageProductsTheirAssociations", get label() { return i18nT("shell.access.manageProductsTheirAssociations", "Manage products & their associations"); }, risk: true, isNew: true },
  ]},
  { id: "onsite", labelKey: "shell.access.onSiteOperations", get label() { return i18nT("shell.access.onSiteOperations", "On-site operations"); }, process: "events", caps: [
    { id: "onsite.walkup",         labelKey: "shell.access.addWalkIns1s", get label() { return i18nT("shell.access.addWalkIns1s", "Add walk-ins & +1s"); }, isNew: true },
    { id: "onsite.queue",          labelKey: "shell.access.manageWalkInQueues", get label() { return i18nT("shell.access.manageWalkInQueues", "Manage walk-in queues"); }, isNew: true },
    { id: "onsite.occupancy",      labelKey: "shell.access.monitorLiveOccupancy", get label() { return i18nT("shell.access.monitorLiveOccupancy", "Monitor live occupancy"); }, isNew: true },
    { id: "onsite.apps.run",       labelKey: "shell.access.runOnSiteApps", get label() { return i18nT("shell.access.runOnSiteApps", "Run on-site apps"); }, isNew: true },
    { id: "onsite.apps.configure", labelKey: "shell.access.configureWhichAppsStaffRun", get label() { return i18nT("shell.access.configureWhichAppsStaffRun", "Configure which apps staff run"); }, risk: true, isNew: true },
  ]},
  { id: "reports", labelKey: "shell.access.reports", get label() { return i18nT("shell.access.reports", "Reports"); }, process: "events", caps: [
    { id: "reports.read",   labelKey: "shell.access.viewDashboards", get label() { return i18nT("shell.access.viewDashboards", "View dashboards"); } },
    { id: "reports.export", labelKey: "shell.access.csvPdfExport", get label() { return i18nT("shell.access.csvPdfExport", "CSV / PDF export"); }, risk: true },
  ]},
  { id: "inbox", labelKey: "shell.access.inbox", get label() { return i18nT("shell.access.inbox", "Inbox"); }, process: "events", caps: [
    { id: "inbox.read",  labelKey: "shell.access.readReplies", get label() { return i18nT("shell.access.readReplies", "Read replies"); } },
    { id: "inbox.write", labelKey: "shell.access.replyToGuests", get label() { return i18nT("shell.access.replyToGuests", "Reply to guests"); }, risk: true },
  ]},
  { id: "compliance", labelKey: "shell.access.compliance", get label() { return i18nT("shell.access.compliance", "Compliance"); }, process: "events", caps: [
    { id: "compliance.read",  labelKey: "shell.access.viewConsentCompliance", get label() { return i18nT("shell.access.viewConsentCompliance", "View consent & compliance"); } },
    { id: "compliance.write", labelKey: "shell.access.configureCompliance", get label() { return i18nT("shell.access.configureCompliance", "Configure compliance"); }, risk: true },
  ]},
  { id: "manage-pages", labelKey: "shell.rail.managePages", get label() { return i18nT("shell.rail.managePages", "Manage Pages"); }, process: "events", caps: [
    { id: "manage_pages.read", labelKey: "shell.access.managePagesRead", get label() { return i18nT("shell.access.managePagesRead", "View Manage Pages"); }, isNew: true },
    { id: "manage_pages.write", labelKey: "shell.access.managePagesWrite", get label() { return i18nT("shell.access.managePagesWrite", "Create, edit and assign Manage Pages"); }, risk: true, isNew: true },
  ]},
  { id: "journeys", labelKey: "shell.access.journeys", get label() { return i18nT("shell.access.journeys", "Booking journeys"); }, process: "events", caps: [
    { id: "journeys.read",    labelKey: "shell.access.viewJourneys", get label() { return i18nT("shell.access.viewJourneys", "View booking journeys"); }, isNew: true },
    { id: "journeys.write",   labelKey: "shell.access.editJourneys", get label() { return i18nT("shell.access.editJourneys", "Create & edit journeys"); }, isNew: true },
    { id: "journeys.publish", labelKey: "shell.access.publishJourneys", get label() { return i18nT("shell.access.publishJourneys", "Publish journeys"); }, risk: true, isNew: true },
    /* New Journeys (2026-08-17) — the unified Journey designer preview. In NO role
     * bundle on purpose: System Administrator resolves to the whole catalog, so the
     * surface is admin-only until the designer graduates (plan v2 phase 6).
     *
     * WITHDRAWN 2026-09-08. `newjourneys.read` was advertised here but declared in
     * no catalog, so it could not be granted — the three designer surfaces gated
     * on it were unreachable for every persona, and this entry offered an operator
     * a checkbox that does nothing. The designer surfaces now gate on
     * `journeys.read` above; a capability the UI advertises must be one the
     * catalog can grant. */
  ]},
  { id: "admin", labelKey: "shell.access.administration", get label() { return i18nT("shell.access.administration", "Administration"); }, process: "events", caps: [
    { id: "settings.read",       labelKey: "shell.access.viewSettings", get label() { return i18nT("shell.access.viewSettings", "View settings"); } },
    { id: "settings.write",      labelKey: "shell.access.editSettings", get label() { return i18nT("shell.access.editSettings", "Edit settings"); }, risk: true },
    { id: "settings.factory_reset", labelKey: "shell.access.factoryResetTenantToSeeds", get label() { return i18nT("shell.access.factoryResetTenantToSeeds", "Factory reset tenant to seeds"); }, risk: true },
    { id: "team.read",           labelKey: "shell.access.viewTeamRoles", get label() { return i18nT("shell.access.viewTeamRoles", "View team & roles"); } },
    { id: "team.write",          labelKey: "shell.access.manageTeamRoles", get label() { return i18nT("shell.access.manageTeamRoles", "Manage team & roles"); }, risk: true },
    { id: "branding.read",       labelKey: "shell.access.viewBranding", get label() { return i18nT("shell.access.viewBranding", "View branding"); } },
    { id: "branding.write",      labelKey: "shell.access.editBranding", get label() { return i18nT("shell.access.editBranding", "Edit branding"); } },
    { id: "locations.read",      labelKey: "shell.access.viewLocations", get label() { return i18nT("shell.access.viewLocations", "View locations"); } },
    { id: "locations.write",     labelKey: "shell.access.manageLocations", get label() { return i18nT("shell.access.manageLocations", "Manage locations"); }, risk: true },
    { id: "billing.read",        labelKey: "shell.access.viewBilling", get label() { return i18nT("shell.access.viewBilling", "View billing"); } },
    { id: "billing.write",       labelKey: "shell.access.manageBilling", get label() { return i18nT("shell.access.manageBilling", "Manage billing"); }, risk: true },
    { id: "integrations.read",   labelKey: "shell.access.viewIntegrations", get label() { return i18nT("shell.access.viewIntegrations", "View integrations"); } },
    { id: "integrations.write",  labelKey: "shell.access.manageIntegrations", get label() { return i18nT("shell.access.manageIntegrations", "Manage integrations"); }, risk: true },
    /* Customer types (2026-09-08, kit e03f10e9). Declared HERE and not only
     * advertised, per the `newjourneys.read` lesson three groups up: the
     * Settings → Customer types tab gates on `customer_types.read`, so the
     * catalog has to be able to grant it or the tab is unreachable for every
     * persona. Company-scoped on the wire; this catalog has no company axis,
     * so the grant is tenant-wide and the screen re-reads on tenant change. */
    { id: "customer_types.read", labelKey: "shell.access.customerTypesRead", get label() { return i18nT("shell.access.customerTypesRead", "View customer types"); }, isNew: true },
    { id: "customer_types.write", labelKey: "shell.access.customerTypesWrite", get label() { return i18nT("shell.access.customerTypesWrite", "Manage customer types"); }, risk: true, isNew: true },
    /* Customer identity (2026-09-09, customer-types wiring kit). Assigning or
     * clearing a customer's type is `customers.write` on the wire — same
     * lesson as above: the drawer gates on it, so the catalog must grant it. */
    { id: "customers.read",  labelKey: "shell.access.customerIdentityRead", get label() { return i18nT("shell.access.customerIdentityRead", "View customer records"); }, isNew: true },
    { id: "customers.write", labelKey: "shell.access.customerIdentityWrite", get label() { return i18nT("shell.access.customerIdentityWrite", "Edit customer records & type"); }, risk: true, isNew: true },
    /* Service record (2026-09-10, customers directory). The customer detail
     * page hides its appointment / event / queue history without this cap, so
     * an undeclared id is indistinguishable from a denial: the System
     * Administrator got `customers.read` from ALL_CAP_IDS and still saw no
     * history. Same lesson as the two above — declare it or it is ungrantable. */
    { id: "customers.service_record.read", labelKey: "shell.access.customerServiceRecordRead", get label() { return i18nT("shell.access.customerServiceRecordRead", "View customer booking & visit history"); }, risk: true, isNew: true },
    { id: "audit.read",          labelKey: "shell.access.viewAuditLog", get label() { return i18nT("shell.access.viewAuditLog", "View audit log"); }, risk: true },
    { id: "audit.read_object",   labelKey: "shell.access.viewAnItemSChangeHistory", get label() { return i18nT("shell.access.viewAnItemSChangeHistory", "View an item's change history"); }, isNew: true },
    { id: "delivery.read",       labelKey: "shell.access.viewDeliveryLog", get label() { return i18nT("shell.access.viewDeliveryLog", "View delivery log"); }, risk: true },
    { id: "observability.read",  labelKey: "shell.access.viewApiObservability", get label() { return i18nT("shell.access.viewApiObservability", "View API activity"); }, risk: true, isNew: true },
    /* Unified administrator, Phase 1 (2026-08-05, operator-directed). The flag
     * `isAdmin()` reads instead of comparing the role NAME. Held only by the
     * System Administrator role, whose perms are the whole catalog anyway — so
     * this is behaviour-neutral today and becomes load-bearing the moment the
     * role is renamed or a second tenant-scoped admin shape appears. Governance
     * affordances (audit trail, AI settings, ticket price-lock, branding audit)
     * all resolve through it. Catalog key pending — fallback carries the English. */
    { id: "admin.super",         labelKey: "shell.access.fullAdministrativeAuthority", get label() { return i18nT("shell.access.fullAdministrativeAuthority", "Full administrative authority"); }, risk: true, isNew: true },
  ]},
  /* ── Workforce process (2026-07-16) ── */
  { id: "wf-scheduling", labelKey: "shell.access.scheduling", get label() { return i18nT("shell.access.scheduling", "Scheduling"); }, process: "workforce", caps: [
    { id: "schedules.read",    labelKey: "shell.access.viewSchedulesShifts", get label() { return i18nT("shell.access.viewSchedulesShifts", "View schedules & shifts"); }, isNew: true },
    { id: "schedules.write",   labelKey: "shell.access.createEditShifts", get label() { return i18nT("shell.access.createEditShifts", "Create & edit shifts"); }, isNew: true },
    { id: "schedules.publish", labelKey: "shell.access.publishSchedulesToStaff", get label() { return i18nT("shell.access.publishSchedulesToStaff", "Publish schedules to staff"); }, risk: true, isNew: true },
    /* Stage 7 (2026-07-29): discarding a week's drafts throws away unpublished work
     * for a whole location, so it is separable from schedules.write. Catalog key
     * not yet shipped (kit-owned) — fallback carries the English (REQ-096i). */
    { id: "schedules.discard", labelKey: "shell.access.discardDraftShifts", get label() { return i18nT("shell.access.discardDraftShifts", "Discard draft shifts"); }, risk: true, isNew: true },
  ]},
  { id: "wf-demand", labelKey: "shell.access.demandForecasting", get label() { return i18nT("shell.access.demandForecasting", "Demand & forecasting"); }, process: "workforce", caps: [
    { id: "demand.read", labelKey: "shell.access.viewDemandForecastCoverage", get label() { return i18nT("shell.access.viewDemandForecastCoverage", "View demand forecast & coverage"); }, isNew: true },
  ]},
  { id: "wf-staffing", labelKey: "shell.access.eventStaffing", get label() { return i18nT("shell.access.eventStaffing", "Event staffing"); }, process: "workforce", caps: [
    { id: "staffing.read",  labelKey: "shell.access.viewEventStaffingPlans", get label() { return i18nT("shell.access.viewEventStaffingPlans", "View event staffing plans"); }, isNew: true },
    { id: "staffing.write", labelKey: "shell.access.editEventRolesAssignments", get label() { return i18nT("shell.access.editEventRolesAssignments", "Edit event roles & assignments"); }, isNew: true },
  ]},
  { id: "wf-ops", labelKey: "shell.access.dayOfOperations", get label() { return i18nT("shell.access.dayOfOperations", "Day-of operations"); }, process: "workforce", caps: [
    { id: "wfops.read", labelKey: "shell.access.viewTheLiveOpsBoard", get label() { return i18nT("shell.access.viewTheLiveOpsBoard", "View the live ops board"); }, isNew: true },
    { id: "wfops.write", labelKey: "shell.access.resolveFloorAlertsApplyReFlow", get label() { return i18nT("shell.access.resolveFloorAlertsApplyReFlow", "Resolve floor alerts & apply re-flow"); }, risk: true, isNew: true },
  ]},
  { id: "wf-requests", labelKey: "shell.access.swapsTimeOff", get label() { return i18nT("shell.access.swapsTimeOff", "Swaps & time off"); }, process: "workforce", caps: [
    { id: "swaps.read",      labelKey: "shell.access.viewSwapTimeOffRequests", get label() { return i18nT("shell.access.viewSwapTimeOffRequests", "View swap & time-off requests"); }, isNew: true },
    { id: "swaps.request",   labelKey: "shell.access.requestSwapsClaimOpenShifts", get label() { return i18nT("shell.access.requestSwapsClaimOpenShifts", "Request swaps & claim open shifts"); }, isNew: true },
    { id: "swaps.approve",   labelKey: "shell.access.approveSwapsTimeOff", get label() { return i18nT("shell.access.approveSwapsTimeOff", "Approve swaps & time off"); }, risk: true, isNew: true },
    { id: "timeoff.request", labelKey: "shell.access.requestTimeOff", get label() { return i18nT("shell.access.requestTimeOff", "Request time off"); }, isNew: true },
  ]},
  { id: "wf-rules", labelKey: "shell.access.laborRulesCompliance", get label() { return i18nT("shell.access.laborRulesCompliance", "Labor rules & compliance"); }, process: "workforce", caps: [
    { id: "laborrules.read",  labelKey: "shell.access.viewLaborRulesViolations", get label() { return i18nT("shell.access.viewLaborRulesViolations", "View labor rules & violations"); }, isNew: true },
    { id: "laborrules.write", labelKey: "shell.access.configureLaborRules", get label() { return i18nT("shell.access.configureLaborRules", "Configure labor rules"); }, risk: true, isNew: true },
  ]},
  { id: "wf-self", labelKey: "shell.access.mySchedule", get label() { return i18nT("shell.access.mySchedule", "My schedule"); }, process: "workforce", caps: [
    { id: "myschedule.read", labelKey: "shell.access.viewOwnShiftsSchedule", get label() { return i18nT("shell.access.viewOwnShiftsSchedule", "View own shifts & schedule"); }, isNew: true },
    { id: "outcomes.write",  labelKey: "shell.access.recordOwnAppointmentOutcomes", get label() { return i18nT("shell.access.recordOwnAppointmentOutcomes", "Record own appointment outcomes"); }, isNew: true },
    /* 2026-08-03 (REQ-126): the staff side of the coaching loop — acknowledge an
     * entry on your OWN thread, mark assigned training complete, add a reflection.
     * Never grants reading or writing anyone else's coaching (that stays
     * coaching.read / coaching.write on the manager side). */
    { id: "coaching.acknowledge_own", labelKey: "shell.access.acknowledgeOwnCoaching", get label() { return i18nT("shell.access.acknowledgeOwnCoaching", "Acknowledge own coaching entries"); }, isNew: true },
  ]},
  /* ── Workforce · bank six-persona port P1 (2026-07-17). Read caps
   * for the pending-port surfaces (see shell/navProfiles.ts + BankPending):
   * enforcement = rail `needs` + ROUTE_READ_PERM; registry entries in
   * skills/role-capability-tracking (nav-only until each port lands). ── */
  { id: "wf-runs", labelKey: "shell.access.generateRunsCopilot", get label() { return i18nT("shell.access.generateRunsCopilot", "Generate runs & copilot"); }, process: "workforce", caps: [
    { id: "runs.read",   labelKey: "shell.access.viewGenerateRunStatusOversight", get label() { return i18nT("shell.access.viewGenerateRunStatusOversight", "View Generate-run status & oversight"); }, isNew: true },
    { id: "copilot.run", labelKey: "shell.access.useTheSchedulingCopilot", get label() { return i18nT("shell.access.useTheSchedulingCopilot", "Use the Scheduling Copilot"); }, isNew: true },
  ]},
  { id: "wf-people", labelKey: "shell.access.peoplePlanning", get label() { return i18nT("shell.access.peoplePlanning", "People & planning"); }, process: "workforce", caps: [
    { id: "advisors.read",     labelKey: "shell.access.viewTheAdvisorRosterPerformance", get label() { return i18nT("shell.access.viewTheAdvisorRosterPerformance", "View the advisor roster & performance"); }, isNew: true },
    { id: "coaching.read",     labelKey: "shell.access.viewCoachingPerformanceInsights", get label() { return i18nT("shell.access.viewCoachingPerformanceInsights", "View coaching & performance insights"); }, isNew: true },
    { id: "assists.read",      labelKey: "shell.access.viewManagerAssistAnalytics", get label() { return i18nT("shell.access.viewManagerAssistAnalytics", "View Manager Assist analytics"); }, isNew: true },
    { id: "requisitions.read", labelKey: "shell.access.viewRequisitionsCandidatePipeline", get label() { return i18nT("shell.access.viewRequisitionsCandidatePipeline", "View requisitions & candidate pipeline"); }, isNew: true },
    { id: "requisitions.write", labelKey: "shell.access.openRequisitionsAdvanceCandidates", get label() { return i18nT("shell.access.openRequisitionsAdvanceCandidates", "Open requisitions & advance candidates"); }, isNew: true },
    { id: "hrplanning.read",   labelKey: "shell.access.viewWorkforcePlanning", get label() { return i18nT("shell.access.viewWorkforcePlanning", "View workforce planning"); }, isNew: true },
  ]},
  { id: "wf-network", labelKey: "shell.access.networkBackOffice", get label() { return i18nT("shell.access.networkBackOffice", "Network & back office"); }, process: "workforce", caps: [
    { id: "region.read",    labelKey: "shell.access.viewRegionalOversightSurfaces", get label() { return i18nT("shell.access.viewRegionalOversightSurfaces", "View regional oversight surfaces"); }, isNew: true },
    { id: "region.write",   labelKey: "shell.access.proposeSecondmentsRedistributeHeadcount", get label() { return i18nT("shell.access.proposeSecondmentsRedistributeHeadcount", "Propose secondments & redistribute headcount"); }, isNew: true },
    { id: "wfreports.read", labelKey: "shell.access.viewWorkforceReporting", get label() { return i18nT("shell.access.viewWorkforceReporting", "View workforce reporting"); }, isNew: true },
    { id: "payroll.read",   labelKey: "shell.access.viewThePayrollBridge", get label() { return i18nT("shell.access.viewThePayrollBridge", "View the payroll bridge"); }, isNew: true },
    { id: "payroll.write",  labelKey: "shell.access.runPayrollReconciliation", get label() { return i18nT("shell.access.runPayrollReconciliation", "Run payroll reconciliation"); }, risk: true, isNew: true },
    { id: "marketplace.write", labelKey: "shell.access.configureTheShiftMarketplace", get label() { return i18nT("shell.access.configureTheShiftMarketplace", "Configure the shift marketplace"); }, isNew: true },
    { id: "setup.write",    labelKey: "shell.access.completeWorkforceSetupSteps", get label() { return i18nT("shell.access.completeWorkforceSetupSteps", "Complete workforce setup steps"); }, isNew: true },
  ]},
  /* ── Queues process (2026-07-17 — queuing first pass; ported 2026-07-23,
   * i18n keys pending — t(key, fallback) self-heals when the catalog lands) ── */
  { id: "q-setup", labelKey: "shell.access.queueSetup", get label() { return i18nT("shell.access.queueSetup", "Queue setup"); }, process: "queues", caps: [
    { id: "queues.read",    labelKey: "shell.access.viewQueuesLiveBoards", get label() { return i18nT("shell.access.viewQueuesLiveBoards", "View queues & live boards"); }, isNew: true },
    { id: "queues.write",   labelKey: "shell.access.createConfigureQueues", get label() { return i18nT("shell.access.createConfigureQueues", "Create & configure queues"); }, isNew: true },
    { id: "queues.publish", labelKey: "shell.access.openPauseCloseQueues", get label() { return i18nT("shell.access.openPauseCloseQueues", "Open / pause / close queues"); }, risk: true, isNew: true },
  ]},
  { id: "q-serve", labelKey: "shell.access.serving", get label() { return i18nT("shell.access.serving", "Serving"); }, process: "queues", caps: [
    { id: "queue.serve",         labelKey: "shell.access.callServeCompleteCustomers", get label() { return i18nT("shell.access.callServeCompleteCustomers", "Call, serve & complete customers"); }, isNew: true },
    { id: "queue.entries.write", labelKey: "shell.access.addEditMoveRemoveQueueEntries", get label() { return i18nT("shell.access.addEditMoveRemoveQueueEntries", "Add, edit, move & remove queue entries"); }, isNew: true },
    { id: "queue.message",       labelKey: "shell.access.twoWayMessageWithCustomersSmsWhatsapp", get label() { return i18nT("shell.access.twoWayMessageWithCustomersSmsWhatsapp", "2-way message with customers (SMS / WhatsApp)"); }, risk: true, isNew: true },
  ]},
  /* `queueflows.write` keeps its id for compatibility: it was minted for the
     Queue Journeys library (removed 2026-08-26) but is the declared
     authorization of four FROZEN workforce walk-in-rule operations, so it stays
     grantable here under the name of what it actually gates. */
  { id: "q-walkin-rules", labelKey: "shell.access.walkInRouting", get label() { return i18nT("shell.access.walkInRouting", "Walk-in routing"); }, process: "queues", caps: [
    { id: "queueflows.write", labelKey: "shell.access.walkInRulesWrite", get label() { return i18nT("shell.access.walkInRulesWrite", "Create & edit walk-in routing rules"); }, risk: true, isNew: true },
  ]},
  { id: "q-insight", labelKey: "shell.access.healthInsight", get label() { return i18nT("shell.access.healthInsight", "Health & insight"); }, process: "queues", caps: [
    { id: "queuehealth.read",       labelKey: "shell.access.viewQueueHealthMetrics", get label() { return i18nT("shell.access.viewQueueHealthMetrics", "View queue health & metrics"); }, isNew: true },
    { id: "queuehealth.alerts",     labelKey: "shell.access.configureAlertsThresholds", get label() { return i18nT("shell.access.configureAlertsThresholds", "Configure alerts & thresholds"); }, risk: true, isNew: true },
    { id: "queuemetrics.export",    labelKey: "shell.access.queueMetricsExport", get label() { return i18nT("shell.access.queueMetricsExport", "Export queue metrics"); }, risk: true, isNew: true },
    { id: "queueinsight.recommend", labelKey: "shell.access.askTheSystemForRecommendations", get label() { return i18nT("shell.access.askTheSystemForRecommendations", "Ask the system for recommendations"); }, isNew: true },
  ]},
  /* ── Store Ops process (2026-07-16, retail fidelity port — spec:
   * the Store Ops fidelity matrix of 2026-07-16) ── */
  { id: "so-core", labelKey: "shell.access.storeOperations", get label() { return i18nT("shell.access.storeOperations", "Store operations"); }, process: "storeops", caps: [
    { id: "storeops.read",      labelKey: "shell.access.viewStoreOpsSurfaces", get label() { return i18nT("shell.access.viewStoreOpsSurfaces", "View store-ops surfaces"); }, isNew: true },
    { id: "storeops.manage",    labelKey: "shell.access.publishRostersManageStoreConfig", get label() { return i18nT("shell.access.publishRostersManageStoreConfig", "Publish rosters & manage store config"); }, risk: true, isNew: true },
    { id: "storeops.floorplan", labelKey: "shell.access.planRostersSkillsAdvisorPerformance", get label() { return i18nT("shell.access.planRostersSkillsAdvisorPerformance", "Plan rosters, skills & advisor performance"); }, isNew: true },
    { id: "storeops.region",    labelKey: "shell.access.viewTheCrossStoreRegion", get label() { return i18nT("shell.access.viewTheCrossStoreRegion", "View the cross-store region"); }, isNew: true },
  ]},
  { id: "so-clienteling", labelKey: "shell.access.clienteling", get label() { return i18nT("shell.access.clienteling", "Clienteling"); }, process: "storeops", caps: [
    { id: "clienteling.read",  labelKey: "shell.access.viewClientBookSessions", get label() { return i18nT("shell.access.viewClientBookSessions", "View client book & sessions"); }, isNew: true },
    { id: "clienteling.write", labelKey: "shell.access.bookSessionsAssignMatchesLogNotes", get label() { return i18nT("shell.access.bookSessionsAssignMatchesLogNotes", "Book sessions, assign matches & log notes"); }, isNew: true },
  ]},
  { id: "so-self", labelKey: "shell.access.myBookAssociate", get label() { return i18nT("shell.access.myBookAssociate", "My book (associate)"); }, process: "storeops", caps: [
    { id: "mybook.read", labelKey: "shell.access.viewOwnDayBookTargetsSchedule", get label() { return i18nT("shell.access.viewOwnDayBookTargetsSchedule", "View own day, book, targets & schedule"); }, isNew: true },
  ]},
  /* ── Support (2026-09-21, Nick) ──────────────────────────────────────────
   * Declared as a real capability rather than left ungated. The operator's
   * ruling is that every signed-in user may report a problem, and the loop
   * below grants it to every role so that is the default answer — but a
   * DECLARED capability is revocable, appears in the permissions UI, and
   * keeps this off the list of operations with no capability at all, which
   * the data model treats as an invariant (capability-scoped authorisation on
   * effectively every operation). An undeclared id is ungrantable; that
   * lesson is written three times above this line. */
  { id: "support", labelKey: "shell.access.support", get label() { return i18nT("shell.access.support", "Support"); }, process: "events", caps: [
    { id: "support.report", labelKey: "shell.access.reportAProblem", get label() { return i18nT("shell.access.reportAProblem", "Report a problem to JRNI support"); }, isNew: true },
  ]},
];

export function processCaps(process: ProcessId): string[] {
  return PERM_CATALOG.filter((g) => g.process === process).flatMap((g) => g.caps.map((c) => c.id));
}
const ALL_CAP_IDS = PERM_CATALOG.flatMap((g) => g.caps.map((c) => c.id));
/* APT-13 ASSIGNMENT BRIDGE (2026-07-26, auth-freeze compliant): the canonical
 * appointments capability family (15 appointments.* caps) + the two roles ship
 * with Claude Code's auth wave — CD does NOT author a PERM_CATALOG group for
 * them (frozen). These four coarse ids gate the new rail items / routes until
 * the canonical catalog lands, then gates re-point to canonical ids and this
 * const + the System Administrator concat below retire (REQ-089 tracks). */
export const APPT_BRIDGE_CAPS = ["appointments.read", "appointments.book", "appointments.checkin", "appointments.manage"];
const CAP_SET = new Set(ALL_CAP_IDS);

/** The support-report capability, and the one place its id is written.
 *  Declared here, above the role store, because `getRolePermsLive()` unions it
 *  in — see the note there. */
export const SUPPORT_REPORT_CAP = "support.report";

/* ─── Role definitions, per process ────────────────────────────────────
 * `perms` is the DEFAULT set; the live editable set lives in ROLE_STORE below. */
export type PillTone = "danger" | "info" | "warn" | "success" | "neutral" | "primary";
export interface RoleDef {
  id: string; process: ProcessId; readonly label: string; labelKey: string; tone: PillTone; color: string;
  blurb: string; perms: string[]; isNew?: boolean; system?: boolean;
}

const EVENTS_CREATOR = [
  "events.read", "events.write", "events.discard_draft",
  "guests.read", "guests.write", "guests.import",
  "campaigns.read", "campaigns.write",
  "flows.read", "flows.write",
  "questions.read", "questions.write",
  "promos.read", "promos.write",
  "surveys.read", "surveys.write",
  // services.read/write DROPPED (Creator role review, 2026-07-10, ingested
  // 2026-07-13): Services is admin-owned setup — the more-powerful Event
  // Manager was already scoped out of it (2026-07-08), so the narrower Creator
  // holding it was inconsistent. Rail hides via the missing cap;
  // NAV_HIDDEN_BY_ROLE["Event Creator"] is belt-and-braces below.
  "reports.read",
  // journeys.read/write added 2026-10-02 (Nick): Add Event opens the Journey designer (route gate journeys.read,
  // document saves journeys.write). journeys.publish NOT granted — Creator authors, a Manager/Admin puts it live.
  "journeys.read", "journeys.write",
];
// Event Coordinator = pure on-site operations, with NO configuration surfaces
// (roles review, 2026-07-08). Scoped to running the room on the day: check-in,
// walk-ups, queues and live occupancy. Deliberately EXCLUDES all Configuration
// items — Messaging (campaigns.*), Customers (guests.read/write), Surveys,
// Services, Locations and Reports were removed so the Coordinator has no config
// menu at all (see NAV_HIDDEN_BY_ROLE). `events.read` is retained so Event Day +
// Calendar context resolve; `guests.checkin` stays so day-of check-in works
// without exposing the Customers list.
const EVENTS_COORDINATOR = [
  "events.read",
  "guests.checkin",
  "guests.approve", // 2026-10-02 reservation workflow (Manager inherits via EVENTS_COORDINATOR)
  "onsite.walkup", "onsite.queue", "onsite.occupancy", "onsite.apps.run",
];
// Event Manager owns an event end-to-end — authoring + on-site + guests + comms
// + reports — but is scoped OUT of the setup/config surfaces an admin owns:
// Services (caps dropped here → Services rail hidden), Resources, the coming-soon
// Queues *process* and Appointments (hidden via NAV_HIDDEN_BY_ROLE). onsite.queue
// is RETAINED — the Event-Day walk-in queue tool stays part of day-of ops
// (operator decision, 2026-07-08). Manager holds NO governance caps, so Settings
// and the admin surfaces stay hidden. Coordinator is now narrow, so Manager
// re-adds the guests/comms/reports/locations/surveys caps explicitly.
const EVENTS_MANAGER_EXCLUDE = new Set(["services.read", "services.write"]);
// inbox.read/write + delivery.read added 2026-07-09 role-access review (ingested
// 2026-07-13): the Manager owns comms end-to-end — can broadcast, so must also be
// able to read two-way replies (Inbox) and audit the delivery/send log.
const EVENTS_MANAGER = [...new Set([...EVENTS_CREATOR, ...EVENTS_COORDINATOR, "guests.read", "guests.write", "campaigns.read", "campaigns.send", "campaigns.send_individual", "inbox.read", "inbox.write", "delivery.read", "reports.read", "reports.export", "locations.read", "surveys.read", "audit.read_object", "observability.read",
  // products.read/write added 2026-10-01 (KP): Event Managers create and edit ticket groups on the Tickets rail
  // (ticket groups ruled onto products.write, 2026-09-30). Side effect, accepted: this also grants the product catalog.
  "products.read", "products.write",
  // journeys.read/write/publish added 2026-10-02: event create/edit moved into the Journey designer
  // (newjourneys, route gate journeys.read; document ops journeys.write, publish journeys.publish) when the
  // wizard was retired 2026-08-24, so without these Add Event landed on "You don't have access to this".
  // Side effect, flagged: the same caps reach queue/appointment journey documents server-side. The
  // standalone journey rail items stay hidden for this role (NAV_HIDDEN_BY_ROLE below).
  "journeys.read", "journeys.write", "journeys.publish"])].filter((p) => !EVENTS_MANAGER_EXCLUDE.has(p));
const EVENTS_APPROVER = [
  "events.read", "events.unpublish", "events.approve",
  "guests.read",
  "campaigns.read", "campaigns.approve",
  "flows.read", "questions.read", "promos.read", "surveys.read", "services.read",
  // reports.export granted 2026-07-09 role-access review (ingested 2026-07-13):
  // the sign-off persona can pull the CSV/PDF audit trail behind a decision.
  "reports.read", "reports.export",
  "compliance.read",
];
// Operations Admin (2026-07-28) — the least-privilege owner of customer journeys
// and the service catalog across the branch network. Deliberately NOT derived
// from System Administrator or any events bundle: this persona must not reach
// Events, Tickets, Calendar, comms or governance. The set below is exactly the
// capability bundle the backend already issues for this role, so cd-app's
// role-fallback resolution and the server's canonical snapshot agree.
const EVENTS_OPERATIONS_ADMIN = [
  "journeys.read", "journeys.write", "journeys.publish",
  "manage_pages.read", "manage_pages.write",
  "services.read", "services.write",
  "locations.read",
  "observability.read",
];

/* Workforce role default sets (2026-07-16). Location scoping (Venue Manager →
 * own locations; Team Member → published-only reads) is server-side. */
const WF_ALL = [
  "schedules.read", "schedules.write", "schedules.publish", "schedules.discard", "demand.read",
  "staffing.read", "staffing.write", "wfops.read",
  /* Staff-facing requests retired 2026-09-14 (KP, Calendar Control port):
     swaps.request and timeoff.request leave every bundle. The MANAGER side
     stays — swaps.read + swaps.approve still feed the Approvals queue.

     PARTIALLY REVERSED 2026-09-16 (CD field report, operator concurred).
     Retiring the capability did not retire the screens it gates, so
     `swaps.request` became defined, still-gating and held by NO role — the
     request controls in WorkforceMyTime and the schedule sandbox rendered for
     nobody. That reads as collateral of a wholesale re-baseline, not as anyone
     deciding "team members may no longer request shift swaps". Regranted below
     to the three staff-facing bundles that held it (Ops Lead, Team Member,
     Personal Banker) — deliberately NOT to this WF_ALL manager baseline.

     `timeoff.request` stays retired everywhere, and that is now a considered
     position rather than the same accident: it gates NOTHING — no `can()` call
     site exists for it anywhere in the app. My Time's Time-off tab gates on
     `swaps.request`, so the regrant above restores time-off requests too. It
     remains a dangling catalogue definition; removing it is a separate, safe
     cleanup nobody has asked for yet. */
  "swaps.read", "swaps.approve",
  "laborrules.read", "laborrules.write", "myschedule.read",
];
const WF_SCHEDULER = [...WF_ALL];
/* Students on the own-day surfaces (2026-09-14, KP): anyone who works a day
 * needs to find and read the student they are seeing — the directory and the
 * record (customers.read) plus their booking history (service_record.read).
 * Read only; the type assignment (customers.write) stays with admins. */
const STUDENT_READ = ["customers.read", "customers.service_record.read", "customer_types.read"];
const WF_VENUE_MANAGER = [...WF_ALL.filter((p) => p !== "laborrules.write"), ...STUDENT_READ];
const WF_OPS_LEAD = ["schedules.read", "staffing.read", "wfops.read", "swaps.read", "swaps.approve", "swaps.request", "myschedule.read"];
/* outcomes.write added 2026-09-14 (KP): My Day captures visit outcomes, and
 * without it the Team Member persona has a read-only day and nothing it can do. */
const WF_TEAM_MEMBER = ["myschedule.read", "schedules.read", "outcomes.write", "swaps.request", ...STUDENT_READ];
const WF_REGIONAL = ["schedules.read", "demand.read", "staffing.read", "wfops.read", "swaps.read", "laborrules.read", "myschedule.read"];

/* Bank role configurations (six-persona port P1, 2026-07-17 —
 * personas verbatim from the WFM exploration workspace; per-role rail lives
 * in shell/navProfiles.ts, plan in the bank "Persona Audit & Plan"). */
const WF_BANK_ADMIN = [...WF_ALL, "copilot.run", "runs.read", "wfreports.read", "payroll.read", "payroll.write", "advisors.read", "marketplace.write", "setup.write"];
/* My calendar is every staff member's own view of their own day, so
 * myschedule.read is a baseline grant, not a manager one (2026-09-14, KP).
 * schedules.read comes with it: everyone with access sees the roster they are
 * rostered on. Both added to the two bundles that lacked them. */
const WF_HRBP = ["hrplanning.read", "advisors.read", "coaching.read", "assists.read", "requisitions.read", "requisitions.write", "myschedule.read", "schedules.read"];
/* Day-of operations added 2026-08-10 (operator): the branch manager runs her
 * branch's floor, so Event Day and the Concierge are hers too — the persona
 * blurb has promised both since P2, and the Floor Calendar's "Open event day" /
 * "Open queue" jump-offs land on their route guards. events.read is the minimum
 * for Event Day context to resolve (same reasoning as EVENTS_COORDINATOR). */
/* wfops.write regranted 2026-09-16 (CD field report): she runs her branch's
   floor, and the Floor screen's "Apply & resolve alert" / re-flow controls gate
   on it — after the 09-14 re-baseline no role held it, so both rendered
   disabled for everyone, with a tooltip naming a capability nobody could have.
   Kept on THIS bundle rather than WF_VENUE_MANAGER so it lands on the branch
   manager alone. */
const WF_BRANCH_MANAGER = [...WF_VENUE_MANAGER, "copilot.run", "advisors.read", "coaching.read", "wfops.write",
  "events.read", "guests.checkin", "onsite.walkup", "onsite.queue", "queue.serve"];
const WF_REGIONAL_BANK = [...WF_REGIONAL, "region.read", "region.write", "runs.read", "coaching.read", "requisitions.read"];
const WF_PERSONAL_BANKER = ["myschedule.read", "schedules.read", "outcomes.write", "swaps.request", "coaching.acknowledge_own", "observability.read"];

/* Store Ops role default sets (2026-07-16). Store scoping is server-side. */
/* Same day-of grant for the store manager (2026-08-10, operator — "branch
 * managers AND store managers"): their rail is the default grouped one, so the
 * Event Day and Concierge items appear via these caps alone. */
const SO_STORE_MANAGER = ["storeops.read", "storeops.manage", "storeops.floorplan", "clienteling.read", "clienteling.write",
  "events.read", "guests.checkin", "onsite.walkup", "onsite.queue", "queue.serve"];
const SO_REGIONAL = ["storeops.read", "storeops.region", "storeops.manage", "clienteling.read", "clienteling.write"];
const SO_ASSOCIATE = ["mybook.read", "clienteling.read", "clienteling.write"];

export const ROLE_CATALOG: Record<ProcessId, RoleDef[]> = {
  events: [
    { id: "Event Creator",        process: "events", labelKey: "shell.access.eventCreator", get label() { return i18nT("shell.access.eventCreator", "Event Creator"); },        tone: "info",    color: "#2D60EB", blurb: "Authors events — builds, submits for approval, publishes, and can discard own drafts.", perms: EVENTS_CREATOR },
    { id: "Event Coordinator",    process: "events", labelKey: "shell.access.eventCoordinator", get label() { return i18nT("shell.access.eventCoordinator", "Event Coordinator"); },    tone: "warn",    color: "#C77F14", blurb: "Runs events on-site: check-in, walk-ups, queues and live occupancy. No configuration access.", perms: EVENTS_COORDINATOR },
    { id: "Event Manager",        process: "events", labelKey: "shell.access.eventManager", get label() { return i18nT("shell.access.eventManager", "Event Manager"); },        tone: "info",    color: "#0E7490", blurb: "Owns an event end-to-end — authoring, guests, comms, on-site day-of ops and reporting. No setup/config surfaces (Services, Resources, Queues, Appointments).", perms: EVENTS_MANAGER, isNew: true },
    { id: "Event Approver",       process: "events", labelKey: "shell.access.eventApprover", get label() { return i18nT("shell.access.eventApprover", "Event Approver"); },       tone: "success", color: "#027D47", blurb: "Reviews & signs off submissions (approve / request changes / reject) and can take a live event offline. Read-only on content — no authoring, sending or publishing.", perms: EVENTS_APPROVER },
    // Perms corrected (2026-07-28) to the canonical Operations Admin bundle: this
    // role must NOT hold events.read (that opens Events/Tickets/Calendar) — it
    // owns journeys.* + services.* + locations.read only, matching the exact
    // bundle the backend issues for events.operations_admin.
    { id: "Operations Admin",     process: "events", labelKey: "shell.access.operationsAdmin", get label() { return i18nT("shell.access.operationsAdmin", "Operations Admin"); },     tone: "info",    color: "#0F766E", blurb: "Maintains customer journeys and the service catalog across the branch network. No events, comms or governance access.", perms: EVENTS_OPERATIONS_ADMIN, isNew: true },
    { id: "System Administrator", process: "events", labelKey: "shell.access.systemAdministrator", get label() { return i18nT("shell.access.systemAdministrator", "System Administrator"); }, tone: "primary", color: "#5142C7", blurb: "Full access — governance, settings, approvals config, all data.", perms: [...ALL_CAP_IDS, ...APPT_BRIDGE_CAPS], system: true },
  ],
  workforce: [
    { id: "Workforce Scheduler", process: "workforce", labelKey: "shell.access.workforceScheduler", get label() { return i18nT("shell.access.workforceScheduler", "Workforce Scheduler"); }, tone: "primary", color: "#5142C7", blurb: "Owns the staffing plan across locations — builds and publishes schedules, tunes rules, decides requests.", perms: WF_SCHEDULER, isNew: true },
    { id: "Venue Manager",       process: "workforce", labelKey: "shell.access.venueManager", get label() { return i18nT("shell.access.venueManager", "Venue Manager"); },       tone: "info",    color: "#0E7490", blurb: "Runs staffing for their location(s): schedules, event staffing, day-of board, swaps and time off.", perms: WF_VENUE_MANAGER, isNew: true },
    { id: "Ops Lead",            process: "workforce", labelKey: "shell.access.opsLead", get label() { return i18nT("shell.access.opsLead", "Ops Lead"); },            tone: "warn",    color: "#C77F14", blurb: "Day-of floor lead — live ops board, schedule context and event staffing readiness. No publishing.", perms: WF_OPS_LEAD, isNew: true },
    { id: "Team Member",         process: "workforce", labelKey: "shell.access.teamMember", get label() { return i18nT("shell.access.teamMember", "Team Member"); },         tone: "success", color: "#027D47", blurb: "Self-service: own shifts, claiming open shifts, swaps and time off. Sees only published schedules.", perms: WF_TEAM_MEMBER, isNew: true },
    { id: "Regional Lead",       process: "workforce", labelKey: "shell.access.regionalLead", get label() { return i18nT("shell.access.regionalLead", "Regional Lead"); },       tone: "info",    color: "#2D60EB", blurb: "Read-across every location: demand, coverage, schedules, requests and compliance posture.", perms: WF_REGIONAL, isNew: true },
    // Bank six-persona port (P1, 2026-07-17) — role configurations behind the bank's per-persona navigation.
    { id: "Workforce Admin",         process: "workforce", labelKey: "shell.access.workforceAdmin", get label() { return i18nT("shell.access.workforceAdmin", "Workforce Admin"); },         tone: "primary", color: "#5142C7", blurb: "Owns the whole workforce platform — schedule, copilot, Generate runs, rules, requests, reporting and the payroll bridge.", perms: WF_BANK_ADMIN, isNew: true },
    { id: "HR Business Partner",     process: "workforce", labelKey: "shell.access.hrBusinessPartner", get label() { return i18nT("shell.access.hrBusinessPartner", "HR Business Partner"); },     tone: "info",    color: "#C13584", blurb: "Workforce planning, advisors, coaching, Manager Assist and requisitions — authors, never publishes.", perms: WF_HRBP, isNew: true },
    { id: "Branch Manager",          process: "workforce", labelKey: "shell.access.branchManager", get label() { return i18nT("shell.access.branchManager", "Branch Manager"); },          tone: "info",    color: "#0E7490", blurb: "One branch end-to-end: floor, schedule, copilot, draft review, forecasts, requests and coaching.", perms: WF_BRANCH_MANAGER, isNew: true },
    { id: "Regional Lead (Banking)", process: "workforce", labelKey: "shell.access.regionalLeadBanking", get label() { return i18nT("shell.access.regionalLeadBanking", "Regional Lead (Banking)"); }, tone: "info",    color: "#2D60EB", blurb: "District read-across with approval posture: regional overview, peer comparison, redistribution and run oversight.", perms: WF_REGIONAL_BANK, isNew: true },
    { id: "Personal Banker",         process: "workforce", labelKey: "shell.access.personalBanker", get label() { return i18nT("shell.access.personalBanker", "Personal Banker"); },         tone: "success", color: "#027D47", blurb: "Self-service front line: My Day, My Performance and My Time.", perms: WF_PERSONAL_BANKER, isNew: true },
  ],
  queues: [
    // Queuing (2026-07-17, restructured same day around administrative queue
    // creation & management — section tabs, not role-specific surfaces).
    // Queue Manager is the default full owner; where queues are locked down an
    // admin narrows the Manager role's live set (Team & Roles) and the Queue
    // Administrator keeps the ceiling. Queue TYPES are development-defined —
    // customers are never exposed to type management (operator, 2026-07-17;
    // queuetypes.manage removed). The former Regional Queue Manager role + Region surface were REMOVED (2026-07-17
    // operator decision) — superseded by the cross-project Reporting feature.
    { id: "Queue Staff",         process: "queues", labelKey: "shell.access.queueStaff", get label() { return i18nT("shell.access.queueStaff", "Queue Staff"); },         tone: "warn",    color: "#C77F14", blurb: "Works the queue at speed — call, serve, complete, move, add walk-ups and 2-way message customers. Also runs their own day and the location's roster: My Day, Floor, Schedule and My calendar, including drafting and publishing shifts. Any device.", perms: ["queues.read", "queue.serve", "queue.entries.write", "queue.message", "myschedule.read", "outcomes.write", "schedules.read", "schedules.write", "schedules.publish", "wfops.read", ...STUDENT_READ], isNew: true },
    /* `journeys.read` + `journeys.write` added 2026-09-15 (PP, seed fix). The
       Queue Manager's blurb has always said she "creates and configures queues
       … designs journeys", and the Queues list opens a queue's details in the
       Journey Designer (`newjourneys`, gated on `journeys.read`) — so without
       these two, clicking her own queue landed on "You don't have access to
       this". The queue's configuration surface IS the designer; a role that owns
       the queue has to be able to open it. Publish is NOT added: shipping the
       queue is `queues.publish`, which she already holds. */
    { id: "Queue Manager",       process: "queues", labelKey: "shell.access.queueManager", get label() { return i18nT("shell.access.queueManager", "Queue Manager"); },       tone: "info",    color: "#0E7490", blurb: "Owns queue health at their location — creates and configures queues, staffs them, designs journeys, watches metrics and resolves issues. May serve, but that's staff's focus.", perms: ["queues.read", "queues.write", "queues.publish", "queue.serve", "queue.entries.write", "queue.message", "queueflows.write", "queuehealth.read", "queuehealth.alerts", "queuemetrics.export", "queueinsight.recommend", "journeys.read", "journeys.write", "observability.read", "myschedule.read", "outcomes.write", "schedules.read", "schedules.write", "schedules.publish", "wfops.read", ...STUDENT_READ], isNew: true },
  ],
  // APT-13 + auth-freeze (2026-07-26, Voyage packet §1): deliberately EMPTY.
  // The canonical roles (appointments.appointment_manager / _staff) + 15-cap
  // family ship with the auth wave — authoring a local role set here would be
  // deleted-on-arrival work. Until then the System Administrator bridge concat
  // (APPT_BRIDGE_CAPS) makes the process demoable; the Fork B1 assignment
  // recommendation lives in REQ-089 / the packet reply, seeded server-side.
  appointments: [],
  storeops: [
    { id: "Store Manager",          process: "storeops", labelKey: "shell.access.storeManager", get label() { return i18nT("shell.access.storeManager", "Store Manager"); },          tone: "warn",    color: "#B08D57", blurb: "Runs the store end-to-end — floor, clienteling, rosters, skills, targets and in-store events.", perms: SO_STORE_MANAGER, isNew: true },
    { id: "Regional Store Manager", process: "storeops", labelKey: "shell.access.regionalStoreManager", get label() { return i18nT("shell.access.regionalStoreManager", "Regional Store Manager"); }, tone: "info",    color: "#8A6A3C", blurb: "The estate one level up — region overview, per-store attainment, clienteling and targets across 5 stores.", perms: SO_REGIONAL, isNew: true },
    { id: "Sales Associate",        process: "storeops", labelKey: "shell.access.salesAssociate", get label() { return i18nT("shell.access.salesAssociate", "Sales Associate"); },        tone: "success", color: "#B08D57", blurb: "The front line — my day, my client book, my targets and my schedule.", perms: SO_ASSOCIATE, isNew: true },
  ],
};

const ALL_ROLE_DEFS: RoleDef[] = Object.values(ROLE_CATALOG).flat();
function roleDefaultPerms(process: ProcessId, role: string): string[] {
  const def = (ROLE_CATALOG[process] || []).find((r) => r.id === role);
  return def ? def.perms : [];
}
export function getRoleDef(role: string, process: ProcessId = "events"): RoleDef | undefined {
  return (ROLE_CATALOG[process] || []).find((r) => r.id === role);
}
export function listRoles(process: ProcessId = "events"): RoleDef[] { return ROLE_CATALOG[process] || []; }
export function listRoleNames(process: ProcessId = "events"): string[] { return (ROLE_CATALOG[process] || []).map((r) => r.id); }

/* ─── Per-role navigation configuration (roles review, 2026-07-08) ──────
 * The authoritative list of rail ids a role should NOT see, independent of
 * capability coupling (some rail items share a read cap with a surface the role
 * keeps — e.g. events.read is shared by Events, Tickets, Calendar). Applied to
 * the desktop IconRail, the mobile drawer AND the launcher so the menu is
 * identical across experiences. Rail ids: see RAIL_ITEMS in chrome-rail.
 * NOTE: `tickets` is hidden for Coordinator + Approver here (this master has a
 * dedicated Tickets rail section that the upstream roles review predated). */
export const NAV_HIDDEN_BY_ROLE: Record<string, string[]> = {
  // Event Coordinator — on-site operations only, NO configuration menu. Hides
  // event setup, tickets, services, resources, appointments, queues, surveys,
  // locations, campaigns, messaging, customers/guests, reports AND the Dashboard
  // (analytics home leaning on reports data the Coordinator can't see). What
  // remains is Event Day (landing) + Calendar (read-only schedule context).
  "Event Coordinator": [
    "events", "tickets", "cs-queues", "cs-services",
    "resources", "surveys", "locations", "campaigns",
    "messaging", "guests", "reports", "dashboard",
  ],
  // Event Manager — owns the event lifecycle (incl. Tickets) but NOT the
  // setup/config surfaces: Services, Resources, and the coming-soon Queues +
  // Appointments processes. Locations stays visible (read-only) for context.
  "Event Manager": [
    "cs-services", "resources", "cs-queues",
    // 2026-10-02: journeys.* is held only so the designer opens for events; the journey library surfaces stay setup.
    "journeys", "new-journeys", "all-journeys",
  ],
  // Event Approver — review-only role that lives in the Approvals queue. Keeps
  // READ caps on every content type (to render the review detail), but the
  // standalone authoring/management surfaces are hidden to keep the menu
  // review-focused. Kept: Approvals (home), Events (browse + take-offline),
  // Reports, Campaigns (regulated-comms context), Dashboard, Calendar.
  "Event Approver": [
    // "tickets" un-hidden 2026-10-02 (Nick): Approvers see Tickets (now an Events tab).
    "messaging", "cs-services", "cs-questions", "surveys", "promos",
    "guests", "cs-queues",
  ],
  // Event Creator — curated menu (Creator role review, 2026-07-10, ingested
  // 2026-07-13). Hides the coming-soon Appointments + Queues process teasers
  // (gated on events.read, which every author holds — they leaked only here)
  // and Services (belt-and-braces: caps dropped above, so this holds even if an
  // admin re-grants services.read to a customised Creator). Everything the
  // Creator authors with (Events, Tickets, Campaigns, Messaging, Questions,
  // Surveys, Promos, Customers, Reports) stays visible.
  "Event Creator": [
    "cs-queues", "cs-services",
    "journeys", "new-journeys", "all-journeys", // 2026-10-02: journeys.* only to open the designer for events
  ],
  // Workforce roles (2026-07-16): Team Member is pure self-service — the
  // events-analytics Dashboard (no cap gate) would be an empty surface, so it
  // hides; landing is My Time. Other workforce roles keep Dashboard.
  "Team Member": ["dashboard"],
  // Queues process (2026-07-17): queue-only personas have no events caps, so
  // the events-analytics Dashboard (no cap gate) would be an empty surface.
  // Keyed on the persona display-role strings whoami returns.
  "Queue Staff · Lobby": ["dashboard"],
  "Queue Staff · Financial aid": ["dashboard"],
  "Queue Staff · Walk-ins": ["dashboard"],
  "Queue Manager · Back Bay": ["dashboard"],
  "Queue Manager · Student Center": ["dashboard"],
  // 3-in-1 rail parity: the retail personas have no Dashboard entry.
  "Store Manager": ["dashboard"],
  "Regional Store Manager": ["dashboard"],
  "Sales Associate": ["dashboard"],
};
export function navHiddenForRole(role?: string): Set<string> {
  return new Set(role ? (NAV_HIDDEN_BY_ROLE[role] || []) : []);
}

/* Default surface a role lands on after sign-in. Event Coordinator lands on
 * Event Day (their primary on-site surface); Event Approver on the Approvals
 * queue (their primary review surface); everyone else on the dashboard.
 *
 * MATCHING IS CASE-INSENSITIVE (2026-07-29). It used to compare the served
 * role label with `===` against a hand-written mix of casings, so a label
 * differing from the table by a single letter's case fell through to
 * `"dashboard"` — silently, with no signal that a mapping had been missed.
 * That is not cosmetic: `dashboard` is itself capability-gated, so a persona
 * without the dashboard capabilities lands on a screen where every tile
 * request 403s (dashboard/summary, dashboard/trends, dashboard/alerts,
 * dashboard/registration-channels, events, storeops/stores) and the first
 * thing the user sees is a page of "Couldn't load …" banners. The
 * authorization behaviour there is CORRECT — the ROUTING is what is wrong,
 * so the fix belongs here and never in a capability check.
 *
 * Two mappings were also genuinely absent, and both fell through to that
 * dashboard 403 storm: "Event Manager" (holds events / guests / campaigns
 * capabilities, no dashboard capability) and "Operations Admin" (holds only
 * journeys.*, services.* and locations.read — the older "Operations admin" →
 * "ops.today" mapping pointed at a workforce surface the role does not hold
 * any capability for). Each now lands on the first surface its own role
 * demonstrably holds a capability for; "Operations Admin" resolves to
 * `journeys`, because `journeys.read` is a capability the role actually
 * holds. */
const LANDING_ROUTE_BY_ROLE: Record<string, string> = {
  // Queuing process (2026-07-17): catalog ids AND the persona display strings.
  // Calendar Control port (2026-09-14, KP): My Day is the landing for every
  // role that has a day of its own. Concierge stays on the rail.
  "queue staff": "workforce.myday",
  "queue manager": "workforce.myday",
  /* "team member" and "venue manager" are deliberately NOT keyed here, though
     the 2026-09-14 Calendar Control port added them: both already existed
     further down THIS object literal from the earlier Workforce port. Duplicate
     keys in one literal fail a real `tsc` build (TS1117) — invisible to
     transpile-only tooling here — and JS last-wins made the new entries dead
     anyway, so the port's stated rule excluded two of the roles it named.
     Deduplicated at source 2026-09-16 (CD build report): each key appears
     exactly once, at the single entry below. */
  "event coordinator": "eventday",
  "event approver": "approvals",
  "event manager": "events",
  // Bank six-persona port (P1): catalog role ids AND the persona
  // display-role strings the sign-in screen passes (e.g. "Workforce admin").
  "workforce admin": "workforce.schedule",
  "branch manager": "workforce.schedule",
  "hr business partner": "workforce.planning",
  "regional lead (banking)": "workforce.region",
  "regional lead · northeast": "workforce.region",
  "personal banker": "workforce.myday",
  "operations admin": "journeys",
  /* Unified administrator, Phase 1 (2026-08-05): the one admin lands on Today in
   * every vertical. Previously absent, so it fell through to `dashboard` — a
   * fallback, never a decision. A tenant's served nav profile may still override
   * this; the profile landing wins when one resolves. */
  "system administrator": "ops.today",
  "workforce scheduler": "workforce.schedule",
  /* Venue Manager keeps the roster, not a personal day: the bundle is WF_ALL
     minus laborrules.write — a manager's landing is the schedule they own. */
  "venue manager": "workforce.schedule",
  "ops lead": "workforce.ops",
  /* Team Member DOES have a day of its own, so the Calendar Control port's rule
     applies and this is My Day — the bundle carries `outcomes.write` precisely
     so that day is not read-only. Changed from
     `workforce.scheduletime.sandbox` 2026-09-16; that sandbox route was the
     pre-port landing and the port intended to replace it, but the duplicate key
     above meant it never did. */
  "team member": "workforce.myday",
  "regional lead": "workforce.forecast",
  "store manager": "storeops.floor",
  "regional store manager": "storeops.region",
  "sales associate": "storeops.my.day",
};
export function landingRouteForRole(role?: string): string {
  const key = (role || "").trim().toLowerCase();
  if (!key) return "dashboard";
  const exact = LANDING_ROUTE_BY_ROLE[key];
  if (exact) return exact;
  /* Location-qualified labels ("Queue Manager · Back Bay", "Personal Banker
   * — Northgate") land where their base role lands. Longest candidate first
   * so "regional lead (banking)" is never shadowed by "regional lead". */
  const candidates = Object.keys(LANDING_ROUTE_BY_ROLE).sort((a, b) => b.length - a.length);
  for (const candidate of candidates) {
    if (key.startsWith(candidate)) return LANDING_ROUTE_BY_ROLE[candidate]!;
  }
  return "dashboard";
}

/* ─── Approval authority, per content type (roles review, 2026-07-08) ──
 * The Approvals review screen gates its decide actions (approve / reject /
 * request-changes) by the right capability for the submission's type. Events
 * and campaigns have first-class approve caps; the other content types have no
 * dedicated approver cap yet, so their approval is admin-only (settings.write).
 * ApproverReviewScreen uses this to render the decision panel read-only for
 * users who may view but not decide (fixes the previously-ungated buttons). */
const APPROVE_CAP: Record<string, string> = {
  events: "events.approve",
  campaigns: "campaigns.approve",
};
export function canApproveType(type: string): boolean {
  const cap = APPROVE_CAP[type];
  return cap ? can(cap) : can("settings.write");
}

/* ─── Live, editable, persisted role store ─────────────────────────────
 * Seeded from the defaults above; an admin can grant/remove caps per role and
 * the change persists + fans out to can()/useUser() everywhere.
 * v9 (2026-07-13, #803): re-seed so Event Manager picks up the new
 * `audit.read_object` default (the merge lets a persisted v8 set win otherwise).
 * v10 (2026-07-13, PM role-access ingest): Manager +inbox.read/write
 * +delivery.read; Approver +reports.export; Creator −services.read/write.
 *
 * ⚠ INTERIM CLIENT-SIDE PERSISTENCE — NOT A BACKEND RECORD (CC GM14 §3 item 3).
 * An admin's runtime capability grants/revokes round-trip through localStorage
 * only: browser-local, non-durable, non-shared, and NOT audit-logged. This is
 * design-preview scaffolding, never a substitute for the identity worker's
 * role/capability store. REQ-088 asks for the real persistence ops
 * (role-permission read/write in the identity worker, capability-gated,
 * audit-chained); when they land, seedRoleStore/persistRoleStore collapse into
 * typed op calls with zero UI change. Until then this store is carved OUT of
 * any "seed-data 0" claim in drop covers — it is disclosed interim state. */
const ROLE_STORE_KEY_BASE = "jrni_role_perms_v10";
const roleStoreKey = (slug: string | null) => `${ROLE_STORE_KEY_BASE}:${slug ?? "none"}`;
type RoleStore = Record<string, string[]>; // `${process}:${role}` -> perms[]
const roleKey = (process: ProcessId, role: string) => `${process}:${role}`;

function seedRoleStore(): RoleStore {
  const s: RoleStore = {};
  ALL_ROLE_DEFS.forEach((d) => { s[roleKey(d.process, d.id)] = [...d.perms]; });
  return s;
}
// Tenant-scoping fix (docket 2026-07-27, item 1 — "role permissions were not
// given tenant-switch handling"): this was ONE flat unscoped key, so a role
// customised under tenant A silently applied to every other tenant in the
// same browser too. ROLE_STORE now starts as pure defaults; the real
// per-tenant load happens once a tenant is CONFIRMED (see the
// onTenantSwitching/onTenantChange registrations after the tenant-identity
// section below) — never eagerly here, when no tenant is known yet.
let ROLE_STORE: RoleStore = seedRoleStore();
let _roleStoreTenantSlug: string | null = null;
function loadRoleStoreForTenant(slug: string | null): void {
  ROLE_STORE = seedRoleStore();
  try {
    const raw = localStorage.getItem(roleStoreKey(slug));
    if (raw) { const saved = JSON.parse(raw) as RoleStore; ROLE_STORE = { ...ROLE_STORE, ...saved }; }
  } catch { /* noop */ }
  _roleStoreTenantSlug = slug;
  /* Calendar Control port (2026-09-14, KP): the workforce and queues defaults
   * changed, and a persisted subset would keep every already-seeded tenant on
   * the old bundles. Drop those two processes' saved entries once per tenant
   * so the new defaults apply; events, store-ops and appointments keep their
   * persisted values, including deliberate narrowings such as Operations
   * Admin's. Keyed per tenant — a browser that only ever loaded tenant A must
   * still migrate tenant B the first time it opens. */
  try {
    const seenKey = `${CC_RESEED_KEY}:${slug ?? "none"}`;
    if (!localStorage.getItem(seenKey)) {
      const fresh = seedRoleStore();
      Object.keys(ROLE_STORE).forEach((k) => {
        if (k.startsWith("workforce:") || k.startsWith("queues:")) ROLE_STORE[k] = [...(fresh[k] ?? [])];
      });
      persistRoleStore();
      localStorage.setItem(seenKey, "1");
    }
  } catch { /* noop */ }
  /* 2026-10-02: a persisted Event Manager set predates the journeys.* grant and shadows the
   * default, so Add Event stayed locked. Union the three caps in once per tenant — additive
   * only, any other narrowing an admin made is kept. */
  try {
    const emKey = `${EM_JOURNEYS_KEY}:${slug ?? "none"}`;
    const k = roleKey("events", "Event Manager");
    if (!localStorage.getItem(emKey)) {
      if (ROLE_STORE[k]) ROLE_STORE[k] = [...new Set([...ROLE_STORE[k], "journeys.read", "journeys.write", "journeys.publish"])];
      const kc = roleKey("events", "Event Creator");
      if (ROLE_STORE[kc]) ROLE_STORE[kc] = [...new Set([...ROLE_STORE[kc], "journeys.read", "journeys.write"])];
      persistRoleStore();
      localStorage.setItem(emKey, "1");
    }
  } catch { /* noop */ }
}
const EM_JOURNEYS_KEY = "jrni_role_perms_em_journeys_2026_10_02b"; // b: + Event Creator
/* Bumped 2026-09-15 (PP): `journeys.read` + `journeys.write` added to the
 * queues Queue Manager so she can open her own queue's details in the Journey
 * Designer. The saved store shadows the catalog, and the 09-14 reseed had
 * already run in this browser, so without a new key the saved subset kept the
 * designer locked for her. */
const CC_RESEED_KEY = "jrni_role_perms_cc_reseed_2026_09_16a";
function persistRoleStore() { try { localStorage.setItem(roleStoreKey(_roleStoreTenantSlug), JSON.stringify(ROLE_STORE)); } catch { /* noop */ } }

export function getRolePermsLive(process: ProcessId, role: string): string[] {
  // System roles (System Administrator) always resolve to the live catalog
  // ceiling, never a persisted subset — a store seeded before newer caps
  // existed would otherwise silently strip them from the admin (admin
  // full-access validation, 2026-07-10, ingested 2026-07-13).
  const def = getRoleDef(role, process);
  if (def?.system) return def.perms;
  const base = ROLE_STORE[roleKey(process, role)] ?? roleDefaultPerms(process, role);
  /* UNIVERSAL CAPABILITY UNION (2026-09-21). `support.report` is granted to
   * every role, and adding it to ROLE_CATALOG defaults was NOT enough: a
   * persisted ROLE_STORE subset — written by any browser that ran this app
   * before the capability existed — shadows the defaults on the line above,
   * so every existing session lost the report button entirely. That is the
   * same failure the CC_RESEED_KEY below was added for, and a reseed would
   * only fix browsers that boot after it.
   *
   * Unioning here instead means no stored subset can strip it, on any
   * browser, ever. A capability that every role holds by definition has no
   * business being revocable by a stale cache — if it is ever made genuinely
   * revocable, this union is the line to remove. */
  return base.includes(SUPPORT_REPORT_CAP) ? base : [...base, SUPPORT_REPORT_CAP];
}
export function setRolePerms(process: ProcessId, role: string, perms: string[]) {
  // A system role's set can never be narrowed or persisted (belt-and-braces
  // with the SettingsTeam UI lock on `system` roles).
  if (getRoleDef(role, process)?.system) return;
  ROLE_STORE[roleKey(process, role)] = [...new Set(perms)].filter((p) => CAP_SET.has(p));
  persistRoleStore(); notify();
}
export function toggleRolePerm(process: ProcessId, role: string, capId: string, on: boolean) {
  const cur = new Set(getRolePermsLive(process, role));
  if (on) cur.add(capId); else cur.delete(capId);
  setRolePerms(process, role, [...cur]);
}
export function resetRole(process: ProcessId, role: string) {
  ROLE_STORE[roleKey(process, role)] = [...roleDefaultPerms(process, role)];
  persistRoleStore(); notify();
}
export function isRoleCustomised(process: ProcessId, role: string): boolean {
  const a = [...getRolePermsLive(process, role)].sort();
  const b = [...roleDefaultPerms(process, role)].sort();
  return JSON.stringify(a) !== JSON.stringify(b);
}

/* Back-compat: ROLE_PERMS[name] reads the LIVE events set; enumerable so
 * `Object.keys(ROLE_PERMS)` and `flatMap` still work. */
export const ROLE_PERMS: Record<string, string[]> = new Proxy({}, {
  get: (_t, k) => getRolePermsLive("events", String(k)),
  has: (_t, k) => listRoleNames("events").includes(String(k)),
  ownKeys: () => listRoleNames("events"),
  getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
}) as Record<string, string[]>;

/* ─── Custom roles ─ admin-saved subsets of a built-in's ceiling ──────── */
export interface CustomRole { perms: string[]; basedOn: string; isCustom: true; }
let CUSTOM_ROLES: Record<string, CustomRole> = {};
export function addCustomRole(name: string, perms: string[], basedOn: string): boolean {
  const trimmed = (name || "").trim();
  if (!trimmed || listRoleNames("events").includes(trimmed) || CUSTOM_ROLES[trimmed]) return false;
  const ceiling = new Set(getRolePermsLive("events", basedOn));
  CUSTOM_ROLES = { ...CUSTOM_ROLES, [trimmed]: { perms: perms.filter((p) => ceiling.has(p)), basedOn, isCustom: true } };
  notify(); return true;
}
export function removeCustomRole(name: string): boolean {
  if (!CUSTOM_ROLES[name]) return false;
  const next = { ...CUSTOM_ROLES }; delete next[name]; CUSTOM_ROLES = next; notify(); return true;
}
export function getCustomRoles(): Record<string, CustomRole> { return CUSTOM_ROLES; }
export function subscribeCustomRoles(fn: () => void) { return subscribe(fn); }

export function getRolePerms(roleKeyName?: string): string[] {
  if (!roleKeyName) return [];
  if (listRoleNames("events").includes(roleKeyName)) return getRolePermsLive("events", roleKeyName);
  if (CUSTOM_ROLES[roleKeyName]) return CUSTOM_ROLES[roleKeyName].perms;
  return [];
}

/* ─── Change bus ─ one subscription for role edits, member edits, user swaps ── */
const subs = new Set<() => void>();
function subscribe(fn: () => void) { subs.add(fn); return () => { subs.delete(fn); }; }
function notify() { subs.forEach((f) => { try { f(); } catch { /* noop */ } }); }
export function subscribeAccess(fn: () => void) { return subscribe(fn); }

/* ─── Tenant identity (#659) ────────────────────────────────────────────────────
 * The session's real tenant, from GET /auth/whoami (`session.tenant.displayName`
 * / `.slug` per the WhoamiResponse contract). NO baked default — while loading
 * (or if whoami is unavailable) this returns null and consumers render nothing
 * rather than a placeholder tenant name. */
export interface TenantIdentity { id: string; slug: string; name: string; plan?: string; }
let TENANT: TenantIdentity | null = null;
let _tenantFetched = false;
/* Identity readiness (JAI-2893, 2026-10-18; additive). Before this, a whoami that
   failed or came back without a tenant was only logged: TENANT stayed null,
   `_tenantFetched` stayed true, nothing retried, and a tenant-gated screen could not
   tell "still resolving" from "never will". Status answers that question, and
   retryTenantIdentity() re-asks whoami once on a person's explicit action.
   "resolved" whenever TENANT is set, whichever path set it (fetchTenant or sign-in). */
export type TenantIdentityStatus = "pending" | "resolved" | "failed" | "tenantless";
let TENANT_STATUS: Exclude<TenantIdentityStatus, "resolved"> = "pending";
export function getTenantIdentityStatus(): TenantIdentityStatus { return TENANT ? "resolved" : TENANT_STATUS; }
/* Identity attempt generation (JAI-2893 r3). fetchTenant's whoami is bound to the
   generation current when it started. Sign-in, session end (sign-out or expiry) and
   every workspace-switch transition (start, confirmation, rollback) supersede it; a
   superseded reply changes nothing: not TENANT, not the active org or its
   translations, not status, and it notifies no one. At most one attempt is in flight. */
let _identityGen = 0;
let _identityInflight = false;
function _supersedeIdentityAttempt() { _identityGen++; _identityInflight = false; }
/** Re-run the initial whoami after it failed or carried no tenant. No-op (false)
 *  while an attempt is in flight or once a tenant is resolved. Never invents a tenant. */
export function retryTenantIdentity(): boolean {
  if (TENANT || _identityInflight) return false;
  _tenantFetched = false;
  TENANT_STATUS = "pending";
  notify();
  fetchTenant();
  return true;
}
// REQ-045: the session's active-organization projection, surfaced at the top
// level of whoami() (organizationId/organizationName, additive-only — both
// absent for a non-demo/unscoped session). Distinct from tenant/workspace:
// an org can be set/cleared within a tenant. Threaded into getTranslationCatalog
// so org-scoped catalog overrides (e.g. a retail org's jargon) apply automatically.
let ACTIVE_ORG_ID: string | null = null;
let ACTIVE_ORG_NAME: string | null = null;
export function getActiveOrganizationId(): string | null { return ACTIVE_ORG_ID; }
export function useActiveOrganization(): { id: string; name: string | null } | null {
  const [, force] = useState(0);
  useEffect(() => { fetchTenant(); return subscribe(() => force((n) => n + 1)); }, []);
  return ACTIVE_ORG_ID ? { id: ACTIVE_ORG_ID, name: ACTIVE_ORG_NAME } : null;
}
function applyActiveOrganization(raw: Record<string, any> | null | undefined): void {
  const src = (raw?.session ?? raw ?? {}) as Record<string, any>;
  const orgId = typeof src.organizationId === "string" ? src.organizationId : null;
  ACTIVE_ORG_ID = orgId;
  ACTIVE_ORG_NAME = typeof src.organizationName === "string" ? src.organizationName : null;
  void refreshTranslationCatalog(orgId);
}
function applyTenantIdentity(raw: Record<string, any> | null | undefined): boolean {
  const t = (raw as any)?.session?.tenant ?? (raw as any)?.tenant;
  if (!t) return false;
  TENANT = {
    id: t.id ?? t.slug ?? "",
    slug: t.slug ?? t.id ?? "",
    name: t.displayName ?? t.display_name ?? t.name ?? t.slug ?? "",
    plan: t.plan ?? undefined,
  };
  return true;
}
function fetchTenant() {
  if (_tenantFetched) return;
  _tenantFetched = true;
  if (typeof whoami !== "function") { TENANT_STATUS = "failed"; notify(); return; }
  const gen = ++_identityGen;
  _identityInflight = true;
  whoami({ baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never)
    .then((r: unknown) => {
      if (gen !== _identityGen) return; // superseded: sign-in, session end or workspace switch since this started
      _identityInflight = false;
      const raw = r as Record<string, any>;
      applyActiveOrganization(raw);
      if (!applyTenantIdentity(raw)) { if (!TENANT) { TENANT_STATUS = "tenantless"; notify(); } return; }
      notify();
      // Initial identity resolution is not a tenant CHANGE. Broadcasting it
      // here caused every screen module imported by SCREENS to run its
      // onTenantChange hydrator, eagerly requesting unrelated admin surfaces
      // that the signed-in persona could not access. Active screens already
      // hydrate through their own hooks. Real workspace switches still call
      // _fireTenantChange() after the replacement session is confirmed.
      //
      // It IS, however, the moment getConfirmedTenantSlug() stops being null,
      // and a read that started before it has no other way to learn that
      // (CD docket 2026-09-15 item 3: on reload the screen mounts first and
      // the tenant arrives second, so the read captured a null scope and
      // discarded its own response). This narrower signal says only "the
      // confirmed slug just appeared" — no hydrator subscribes to it, so the
      // eager-prefetch regression the paragraph above describes cannot return.
      _fireTenantIdentityResolved();
    })
    .catch((e: unknown) => {
      if (gen !== _identityGen) return; // superseded — a newer identity owns status now
      _identityInflight = false;
      console.warn("whoami failed — tenant identity unavailable:", e);
      if (!TENANT) { TENANT_STATUS = "failed"; notify(); }
    });
}
/** The signed-in tenant, or null while loading. Re-renders when it resolves. */
export function getActiveTenantSlug(): string | null { return TENANT?.slug ?? null; }
export function useTenant(): TenantIdentity | null {
  const [, force] = useState(0);
  useEffect(() => { fetchTenant(); return subscribe(() => force((n) => n + 1)); }, []);
  return TENANT;
}

/* ─── Workspaces the user can access (account switcher) ──────────────────
 * The account-list dropdown + Account › Workspaces read the same canonical list:
 * getAccountDetails().workspaces (AccountWorkspace[]). Fetched once and cached.
 * `switchWorkspace` re-scopes the active workspace — see the note on the real op. */
export interface Workspace {
  id: string; slug: string; name: string; brandColor: string; initials: string;
  plan?: string; role?: string; eventCount?: number; current: boolean;
}
const ACCOUNT_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
function toWorkspace(w: Record<string, any>): Workspace {
  const name = w.displayName || w.name || w.slug || "";
  return {
    id: w.id ?? w.slug ?? "", slug: w.slug ?? w.id ?? "", name,
    brandColor: w.brandColor || T.p1, initials: w.initials || initialsOf(name),
    plan: w.plan ?? undefined, role: w.role ?? undefined,
    eventCount: typeof w.eventCount === "number" ? w.eventCount : undefined,
    current: !!w.current,
  };
}
/* ─── Session end (CC, 2026-10-10) ─────────────────────────────────────────
   onSessionEnd fires when a signed-in session ends, by sign-out or by expiry, so a store can drop
   what it holds for that person: reads kept in memory and choices kept in browser storage. Nothing
   fires at boot, before anyone has signed in, so a reload keeps what a signed-in person left.
   getSessionEpoch() moves on every session end and the instant a workspace switch starts. An export
   that waits on a read compares it before handing over the file, so a file the previous session
   asked for is never given to the next one. */
let SESSION_EPOCH = 0;
const _sessionEndSubs = new Set<() => void>();
export function getSessionEpoch(): number { return SESSION_EPOCH; }
export function onSessionEnd(fn: () => void): () => void {
  _sessionEndSubs.add(fn);
  return () => { _sessionEndSubs.delete(fn); };
}
function _fireSessionEnd() {
  SESSION_EPOCH++;
  _supersedeIdentityAttempt(); // a whoami begun for the ended session must not restore its identity
  _sessionEndSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onSessionEnd handler failed:", e); } });
}

let WORKSPACES: Workspace[] = [];
let _workspacesFetched = false;
/* The previous person's workspace memberships are dropped at session end (CC, 2026-10-10), and a
   read that lands after it does not refill them; the switcher reads them again for the next person. */
let _workspacesGen = 0;
onSessionEnd(() => { WORKSPACES = []; _workspacesFetched = false; _workspacesGen++; });
function fetchWorkspaces() {
  if (_workspacesFetched) return;
  _workspacesFetched = true;
  if (typeof getAccountDetails !== "function") return;
  const gen = _workspacesGen;
  (getAccountDetails as (o: never) => Promise<unknown>)(ACCOUNT_OPTS)
    .then((r: unknown) => {
      if (gen !== _workspacesGen) return; // the session ended while this read was in flight
      const raw = r as Record<string, any>;
      const acct = raw.account ?? raw.data ?? raw;
      const rows: any[] = Array.isArray(acct?.workspaces) ? acct.workspaces : [];
      WORKSPACES = rows.map(toWorkspace);
      notify();
    })
    .catch((e: unknown) => console.warn("getAccountDetails (workspaces) failed:", e));
}
/** The workspaces the current user can switch between. Re-renders when loaded. */
export function useWorkspaces(): Workspace[] {
  const [, force] = useState(0);
  useEffect(() => { fetchWorkspaces(); return subscribe(() => force((n) => n + 1)); }, []);
  return WORKSPACES;
}

/** Subscribe to be notified exactly when the ACTIVE TENANT changes (a
 * workspace switch) — narrower than the general `subscribe` pub-sub above,
 * which fires on every access-state change. Registrants use this to purge
 * tenant-scoped module caches SYNCHRONOUSLY on an in-place switch (the
 * store-switcher cross-tenant leak, docket 2026-07-26 CD-PUSHBACK-STORE-
 * SWITCHER: `switchWorkspace` doesn't reload the page in this preview, so a
 * module-scope cache that isn't keyed by tenant identity survives into the
 * new tenant's session). Returns an unsubscribe fn. */
const _tenantChangeSubs = new Set<() => void>();
/** Subscribe to be notified the INSTANT a tenant switch is optimistically
 * initiated — BEFORE the session-switch op resolves. Registrants purge
 * tenant-scoped module caches SYNCHRONOUSLY here (never show tenant A's data
 * under tenant B's chrome) WITHOUT issuing any new network request yet —
 * fetching happens only once onTenantChange (below) confirms the new session
 * is actually live. This is the fix for the request-order race in docket
 * 2026-07-27 (CD-DOCKET-OVERNIGHT item 1): a request fired at THIS instant
 * would still run under the OUTGOING tenant's session against a real
 * backend, yet would be accepted as the incoming tenant's data because the
 * client-side slug already matches. Returns an unsubscribe fn. */
const _tenantSwitchingSubs = new Set<() => void>();
export function onTenantSwitching(fn: () => void): () => void {
  _tenantSwitchingSubs.add(fn);
  return () => { _tenantSwitchingSubs.delete(fn); };
}
function _fireTenantSwitching() {
  SESSION_EPOCH++; // an export begun under the outgoing workspace is not handed over under the incoming one
  _supersedeIdentityAttempt(); // nor is an identity reply begun under it applied to the incoming one
  _tenantSwitchingSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onTenantSwitching handler failed:", e); } });
}

let _tenantGeneration = 0;
let _switchPending = false;
/** Increments every time the CONFIRMED active tenant changes (initial
 * resolution, a completed switch, or a rollback). */
export function getTenantGeneration(): number { return _tenantGeneration; }
/** The CONFIRMED active tenant slug — null before anything has resolved, AND
 * null for the duration of a pending workspace switch (between the
 * optimistic update and its confirmation/rollback). Tenant-scoped stores
 * should key and gate their fetches off THIS, never off the optimistic
 * identity `useTenant()`/the chrome reads for instant display. */
export function getConfirmedTenantSlug(): string | null {
  return _switchPending ? null : getActiveTenantSlug();
}

/** Subscribe to be notified once the ACTIVE TENANT is CONFIRMED — initial
 * whoami resolution, a successful workspace-switch confirmation, or a
 * rollback to the previous tenant after a failed switch. This is the
 * "safe to fetch" signal: by the time this fires, `getConfirmedTenantSlug()`
 * reflects a session that is actually live server-side, never optimistic.
 * Registrants purge on `onTenantSwitching` and (re)fetch here. Returns an
 * unsubscribe fn. */
export function onTenantChange(fn: () => void): () => void {
  _tenantChangeSubs.add(fn);
  return () => { _tenantChangeSubs.delete(fn); };
}
function _fireTenantChange() {
  _switchPending = false;
  _tenantGeneration++;
  _supersedeIdentityAttempt(); // confirmed (or rolled-back) tenant wins over any reply still in flight
  _tenantChangeSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onTenantChange handler failed:", e); } });
}

/** Fires once, on the FIRST whoami resolution, when the confirmed tenant slug
 * goes from null to a real slug. Deliberately NOT onTenantChange: it does not
 * bump the tenant generation and it is not a switch, so nothing that hydrates
 * on a workspace change wakes up. Subscribe only if you need to recover from
 * having read the slug while it was still null — the scoped-read helper in
 * screens/customers/customers-data.ts is the intended (and expected only)
 * consumer. Returns an unsubscribe fn. */
const _tenantIdentityResolvedSubs = new Set<() => void>();
export function onTenantIdentityResolved(fn: () => void): () => void {
  _tenantIdentityResolvedSubs.add(fn);
  return () => { _tenantIdentityResolvedSubs.delete(fn); };
}
function _fireTenantIdentityResolved() {
  _tenantIdentityResolvedSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onTenantIdentityResolved handler failed:", e); } });
}

/** Switch the active workspace (REQ-006 / #account-switch, resolved 2026-07-05).
 *
 * The real `switchWorkspace` op is a NAVIGATION resolver, not an in-place
 * re-scope: production sessions are per-tenant-slug cookies, so it validates the
 * target and hands back whoami-shaped hydration + the target `studioHost` to
 * navigate to. We optimistically update the chrome immediately, then resolve
 * against the identity worker; if it returns a real `studioHost` URL we navigate
 * there (that's what actually re-scopes the data plane). In the in-browser
 * preview the mock returns a scheme-less host, so we stay put with the optimistic
 * update — the switcher + Account › Workspaces call sites never change. */
let _switchGen = 0;
export function switchWorkspace(workspaceId: string): void {
  const ws = WORKSPACES.find((w) => w.id === workspaceId);
  if (!ws || ws.current) return;
  const prevTenant = TENANT;
  const prevWorkspaces = WORKSPACES;
  const gen = ++_switchGen; // generation guard (docket 2026-07-27 item 1): a stale switch's late response must never win over a newer one
  WORKSPACES = WORKSPACES.map((w) => ({ ...w, current: w.id === workspaceId }));
  TENANT = { id: ws.id, slug: ws.slug, name: ws.name, plan: ws.plan }; // optimistic — chrome (name/avatar) updates instantly
  _switchPending = true; // no tenant-scoped store may fetch until this clears
  notify();
  _fireTenantSwitching(); // purge every registered cache SYNCHRONOUSLY — never show tenant A's data under tenant B's chrome — nobody fetches yet
  if (typeof switchWorkspaceOp !== "function") { _fireTenantChange(); return; } // no real op wired — treat the optimistic state as confirmed so stores aren't stuck empty forever
  (switchWorkspaceOp as (id: string, o: never) => Promise<unknown>)(workspaceId, ACCOUNT_OPTS)
    .then((r: unknown) => {
      if (gen !== _switchGen) return; // superseded by a newer switch — a stale confirmation must never fire
      const res = r as Record<string, any>;
      const wsr = (res?.workspace ?? {}) as Record<string, any>;
      TENANT = {
        id: wsr.id ?? ws.id, slug: wsr.slug ?? ws.slug,
        name: wsr.displayName ?? wsr.name ?? ws.name, plan: wsr.plan ?? ws.plan,
      };
      if (res?.whoami) applyWhoamiUser(res.whoami);
      applyActiveOrganization(res?.whoami);
      notify();
      _fireTenantChange(); // session confirmed live — safe for every tenant-scoped store to fetch tenant B's data now
      const host = res?.studioHost;
      if (typeof host === "string" && /^https?:\/\//.test(host) && typeof window !== "undefined") {
        window.location.assign(host);
      }
    })
    .catch((e: unknown) => {
      if (gen !== _switchGen) return; // a newer switch already superseded this attempt
      console.warn("switchWorkspace failed:", e);
      // Roll back the optimistic identity (docket 2026-07-27 item 1) — never
      // leave the chrome claiming a tenant whose session never actually switched.
      WORKSPACES = prevWorkspaces;
      TENANT = prevTenant;
      notify();
      toast({ tone: "danger", title: i18nT("shell.access.couldnTSwitchWorkspaces", "Couldn't switch workspaces"), subtitle: i18nT("shell.access.youReStillOnThePreviousWorkspaceRetryInAMoment", "You're still on the previous workspace. Retry in a moment.") });
      _fireTenantSwitching(); // purge again — clear anything a store began showing for the failed target tenant
      _fireTenantChange(); // back on the previous (already-live) tenant — safe to reload its data immediately
      // Rolled back to no tenant while identity was still unresolved: the switch
      // superseded the only lookup, so ask once more. A failed/tenantless result
      // already recorded is kept; its retry stays the person's explicit action.
      if (!TENANT && TENANT_STATUS === "pending") { _tenantFetched = false; fetchTenant(); }
    });
}

/* ─── Current user + effective permissions ─────────────────────────────
 * A user has a role per process (assignments) plus additive grants/revokes.
 * Effective = ∪(role defaults over assigned processes) ∪ grants − revokes. */
export interface CurrentUser {
  id?: string; name?: string; email?: string; role?: string;
  /** Raw tenant membership role from the verified whoami session. */
  workspaceRole?: string | null;
  init?: string; color?: string;
  /** Role per process. Falls back to { events: role } when absent. */
  assignments?: Partial<Record<ProcessId, string>> | null;
  /** Additive per-user capabilities above the role. */
  grants?: string[] | null;
  /** Capabilities removed from the role for this user. */
  revokes?: string[] | null;
  /** Direct snapshot (impersonation / tenant override) — wins over role resolution. */
  permissions?: string[] | null;
  locations?: string[] | null;
}

export function effectivePerms(u: { role?: string; assignments?: Partial<Record<ProcessId, string>> | null; grants?: string[] | null; revokes?: string[] | null }): Set<string> {
  const assignments = u.assignments && Object.keys(u.assignments).length ? u.assignments : (u.role ? { events: u.role } : {});
  const set = new Set<string>();
  (Object.entries(assignments) as [ProcessId, string][]).forEach(([proc, role]) => {
    getRolePermsLive(proc, role).forEach((p) => set.add(p));
  });
  (u.grants || []).forEach((p) => { if (CAP_SET.has(p)) set.add(p); });
  (u.revokes || []).forEach((p) => set.delete(p));
  return set;
}

// #655 (2026-07-04): no baked identity in app source. CURRENT_USER is empty
// until a demo sign-in mints a session; signIn() hydrates it from whoami().
let CURRENT_USER: CurrentUser = {};

export function setUser(u: CurrentUser) { CURRENT_USER = u; notify(); }
export function getUser(): CurrentUser { return CURRENT_USER; }

/** Re-renders on any access change (user / role edit / member edit / impersonation). */
export function useUser(): CurrentUser {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return CURRENT_USER;
}

/* Grant it to every role in every process, including roles added later in this
 * file's lifetime, by rewriting each default set once at module load. A new
 * array per role — several roles share the same perms const (WF_BRANCH_MANAGER
 * spreads WF_VENUE_MANAGER, and so on), so mutating in place would grant it
 * several times and, worse, edit a list another role is still reading.
 *
 * This keeps the DEFAULTS honest, so the permissions UI shows the capability
 * held. It is not what makes the gate pass — `getRolePermsLive()` does that,
 * because defaults alone are invisible to a browser with a persisted store. */
for (const defs of Object.values(ROLE_CATALOG)) {
  for (const def of defs) def.perms = [...new Set([...def.perms, SUPPORT_REPORT_CAP])];
}

/** Permission check — reads the current user. Direct snapshot wins; else effective. */
export function can(perm: string): boolean {
  const u = CURRENT_USER;
  if (Array.isArray(u.permissions) && u.permissions.length) return u.permissions.includes(perm);
  return effectivePerms(u).has(perm);
}

/** True when the current (or impersonated) user holds full administrative
 * authority. Phase 1 (2026-08-05): this was a comparison against the literal
 * role name "System Administrator", which meant renaming the role silently
 * disabled every governance affordance hanging off it (audit trail, AI
 * settings, ticket price-lock, branding audit). It is now a capability check,
 * so the role's NAME and its AUTHORITY are no longer the same fact. */
export function isAdmin(): boolean {
  return can("admin.super");
}

/**
 * May this user report a problem to JRNI support?
 *
 * Only when the SERVER's permission list for the person at the keyboard carries
 * support.report. The role catalogue is not consulted: it unions support.report
 * into every role (see the loop above), so a session with no served list (a demo
 * persona sign-in, or a whoami without permissions, which applyWhoamiUser stores
 * as null) and a "View as" target (whose list startImpersonation builds from that
 * catalogue) would both be offered a send that cannot succeed while the report
 * endpoint is not live (CC 0081, 0088, 0102).
 */
export function canReportProblem(): boolean {
  const served = getRealUser().permissions;
  return Array.isArray(served) && served.includes(SUPPORT_REPORT_CAP);
}

/* ─── Team members ─ API-backed store the admin screen edits ───────────
 * #651/#659 (2026-07-04): the fictional seeded roster was removed — members
 * hydrate from the real `listTeamMembers` client op.
 *
 * UIR-01 round 2 (2026-07-25): the member WRITES are now the real ops too —
 * `inviteTeamMember` / `updateTeamMemberRole` / `resendTeamInvite` /
 * `removeTeamMember` are declared in the contract, exported from the typed
 * client and fully mock-served. (The earlier remediation pass disabled these
 * CTAs as "no declared op yet" on a stale premise — see the scrub audit.)
 *
 * Role vocabulary: team administration is TENANT-role scoped (`roleDefinitions`
 * on the list response — owner/admin/member/…), which is a DIFFERENT namespace
 * from this file's Events-PROCESS role catalog that `can()` resolves against.
 * Writes therefore carry the API role KEY (`Member.roleKey`), never the local
 * process-role label — no invented mapping between the two. */
export interface Member {
  id: string; name: string; email: string; init: string; color: string;
  /** The identity user id (TeamMember.userId), which staff records link to (CC 0374). Absent on non-API rows. */
  userId?: string | null;
  /** Display role label — the tenant role's label when API-sourced, else the process role. */
  role: string;
  /** Tenant role key as the API knows it (`roleDefinitions[].key`). Empty for non-API rows. */
  roleKey: string;
  /** Events-PROCESS role this member maps onto (what `can()`/impersonation resolve
   *  against). Empty when the tenant role has no process-role counterpart. */
  processRole: string;
  /** Membership status from the API (invited rows can be resent). */
  status: string;
  /** Capability ids the API reports for this member (authoritative when API-sourced). */
  apiCaps: string[] | null;
  /** Role per process, verbatim from the seed persona (the SAME vocabulary
   *  `can()` resolves — what capability counts and impersonation read). Null for
   *  rows from sources that predate assignments. */
  assignments: Partial<Record<ProcessId, string>> | null;
  grants: string[]; revokes: string[];
  last: string;
}

/** A tenant role as the team API defines it — the vocabulary team writes use. */
export interface ApiRole {
  key: string; label: string; description: string; isCustom: boolean;
  capabilities: string[]; permissions: string[]; memberCount: number;
}
let API_ROLES: ApiRole[] = [];
/** Tenant roles from the last `listTeamMembers` response (empty until it lands). */
export function listApiRoles(): ApiRole[] { return API_ROLES; }
export function getApiRole(key: string): ApiRole | undefined { return API_ROLES.find((r) => r.key === key); }
const TEAM_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
const MEMBER_COLORS = ["#5142C7", "#1F8A5B", "#C77F14", "#C13584", "#2D60EB", "#8955F2", "#0E7490", "#D01647"]; // ui-config
/* ── WHO IS SIGNED IN, FOR THEIR PORTRAIT ─────────────────────────────────────
 * The headshots are filed by PERSONA id (`app/public/avatars/wf-ms-team.png`),
 * but `whoami` hydrates the current user from the session and its `id` is the
 * account's, not the persona's — so keying the portrait off `user.id` showed
 * initials everywhere except the sign-in screen. The persona id is remembered at
 * sign-in instead, and kept in sessionStorage so a reload still knows whose face
 * to show. Cleared on sign-out.
 *
 * `portraitSrc()` with no argument means "the signed-in person"; passing an id
 * asks for that person's file. A missing file is not a problem — every consumer
 * falls back to the initials disc (Avatar's own onError, or ours). */
const PORTRAIT_KEY = "studio.signedInPersonaId";
/* The signed-in persona's own photo (CC, 2026-10-05): /demo/personas carries a `photoUrl` from the persona's
   staff record. It is remembered beside the persona id and used before the static avatar file, which only a
   few seeded personas have. Cleared with the id on sign-out. Only an https or same-origin URL is kept. */
const PORTRAIT_URL_KEY = "studio.signedInPersonaPhoto";
let PERSONA_ID: string | null = (() => {
  try { return sessionStorage.getItem(PORTRAIT_KEY); } catch { return null; }
})();
let PERSONA_PHOTO: string | null = (() => {
  try { return sessionStorage.getItem(PORTRAIT_URL_KEY); } catch { return null; }
})();
/** A photo or logo URL that is safe to show: https, or a path on this origin. Anything else is dropped. */
export function safeImageUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url, window.location.href);
    return u.protocol === "https:" || u.origin === window.location.origin ? url : null;
  } catch { return null; }
}
export function setSignedInPersonaId(id: string | null, photoUrl?: string | null) {
  PERSONA_ID = id;
  PERSONA_PHOTO = id ? safeImageUrl(photoUrl) : null;
  try { if (id) sessionStorage.setItem(PORTRAIT_KEY, id); else sessionStorage.removeItem(PORTRAIT_KEY); } catch { /* private mode */ }
  try { if (PERSONA_PHOTO) sessionStorage.setItem(PORTRAIT_URL_KEY, PERSONA_PHOTO); else sessionStorage.removeItem(PORTRAIT_URL_KEY); } catch { /* private mode */ }
}
export function portraitSrc(id?: string): string | undefined {
  if (!id && PERSONA_PHOTO) return PERSONA_PHOTO;
  const key = id || PERSONA_ID;
  if (!key) return undefined;
  const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
  return `${base}/avatars/${key}.png`;
}

export function initialsOf(s: string) { return (s || "?").split(/[@.\s-]+/).filter(Boolean).map((p) => p[0]).join("").toUpperCase().slice(0, 2) || "?"; }
export function colorFor(s: string) { return MEMBER_COLORS[(s || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % MEMBER_COLORS.length]; }

/* API role slug → events-process role label. */ // ui-config
const API_ROLE_MAP: Record<string, string> = {
  admin: "System Administrator", administrator: "System Administrator",
  manager: "Event Manager", coordinator: "Event Coordinator",
  creator: "Event Creator", editor: "Event Creator", approver: "Event Approver",
};
function toMemberFromApi(m: Record<string, any>): Member {
  const name = (m.displayName ?? m.name ?? (`${m.firstName ?? m.first_name ?? ""} ${m.lastName ?? m.last_name ?? ""}`).trim()) || m.email || "";
  const roleRaw = (m.role ?? m.roleId ?? "").toString();
  // Prefer the tenant role's own label from roleDefinitions; fall back to the
  // legacy slug→process-role map, then the raw key.
  const apiRole = getApiRole(roleRaw);
  // Persona-consolidation alignment (2026-08-06): rows served from personas.json
  // carry `assignments` (per-process roles) and `displayRole` (the persona's own
  // label). When assignments are present THEY decide the process role — the
  // legacy slug→events map must not run, or every rbacLevel:"admin" persona
  // would silently resolve to System Administrator.
  const rawAssign = (m.assignments && typeof m.assignments === "object") ? m.assignments as Record<string, string> : null;
  const assignments: Partial<Record<ProcessId, string>> = {};
  if (rawAssign) {
    (Object.entries(rawAssign) as [ProcessId, string][]).forEach(([proc, r0]) => {
      if (PROCESSES.some((p) => p.id === proc) && typeof r0 === "string" && r0) assignments[proc] = r0;
    });
  }
  const displayRole = typeof m.displayRole === "string" && m.displayRole ? m.displayRole : "";
  return {
    id: m.id ?? "m-" + (m.email ?? name).split("@")[0],
    userId: m.userId ?? m.user_id ?? null,
    name, email: m.email ?? "",
    init: initialsOf(name), color: colorFor(m.email ?? name),
    role: displayRole || (apiRole?.label ?? API_ROLE_MAP[roleRaw.toLowerCase()] ?? roleRaw),
    roleKey: roleRaw,
    processRole: assignments.events ?? (rawAssign ? "" : (API_ROLE_MAP[roleRaw.toLowerCase()] ?? "")),
    assignments: rawAssign ? assignments : null,
    status: (m.status ?? "active").toString(),
    /* An EMPTY capability array is not an authoritative "zero capabilities" —
       the served roster carries [] because process roles resolve client-side.
       Treating it as authoritative printed "0 caps" against real people. */
    apiCaps: Array.isArray(m.capabilities) && m.capabilities.length ? m.capabilities as string[] : null,
    grants: m.grants ?? [], revokes: m.revokes ?? [],
    last: m.lastActiveLabel ?? m.lastActiveAt ?? m.last_active_at ?? m.last ?? "—",
  };
}

let MEMBERS: Member[] = [];
let _membersFetched = false;
/* A FAILED member read is not "no members" (2026-08-06). It used to vanish into
 * a console.warn, so every consumer saw an empty roster and had no way to tell
 * the two apart — People then rendered eleven staff records and asserted that
 * real, logged-in people had no sign-in. Exposed so a screen can disclose it. */
let MEMBERS_ERROR: string | null = null;
/* The team roster (every member's name, email and role) is dropped when the session ends, by sign-out
   or expiry (CC, 2026-10-08), so the next person signing in to the same workspace is never shown the
   previous one's read. The fetched flag is cleared, not set, so the next screen that needs the roster
   reads it again; a read that lands after the session ended does not refill it. */
let _membersGen = 0;
subscribe(() => { if (!SIGNED_IN && (MEMBERS.length || _membersFetched || MEMBERS_ERROR)) { MEMBERS = []; MEMBERS_ERROR = null; _membersFetched = false; _membersGen++; } });
/** Set only when `listTeamMembers` actually rejected; null while loading or on success. */
export function getMembersError(): string | null { return MEMBERS_ERROR; }
/* The WHOLE team, page by page (CC 0381), as listAllStaff reads the staff roster: limit 100, stop when
   hasMore is false, and reject the whole read when the paging does not add up, so a partial team is never
   held as the complete one. Role definitions come from page 1. */
const TEAM_PAGE_LIMIT = 100;
const TEAM_MAX_PAGES = 200;
async function listAllTeamMembers(isCurrent: () => boolean): Promise<{ members: any[]; roleDefinitions: any[] }> {
  const members: any[] = [];
  const seen = new Set<string>();
  let roleDefinitions: any[] = [];
  let total: number | null = null;
  for (let page = 1; page <= TEAM_MAX_PAGES; page += 1) {
    if (!isCurrent()) throw new Error("team read superseded");
    const raw = (await listTeamMembers({ page, limit: TEAM_PAGE_LIMIT }, TEAM_OPTS)) as unknown as Record<string, any>;
    const rows: any[] = raw?.members;
    const pg = raw?.pagination as Record<string, any> | undefined;
    if (!Array.isArray(rows)) throw new Error(`team page ${page}: response has no members array`);
    if (!pg || typeof pg.hasMore !== "boolean") throw new Error(`team page ${page}: pagination.hasMore is missing`);
    if (pg.page !== undefined && pg.page !== page) throw new Error(`team page ${page}: answered as page ${String(pg.page)}`);
    if (!Number.isInteger(pg.total) || pg.total < 0) throw new Error(`team page ${page}: pagination.total is missing`);
    if (total === null) total = pg.total as number;
    else if (pg.total !== total) throw new Error(`team page ${page}: total changed during the read (${total} → ${String(pg.total)})`);
    if (page === 1 && Array.isArray(raw.roleDefinitions)) roleDefinitions = raw.roleDefinitions;
    for (const m of rows) {
      const id = String(m?.id ?? "");
      if (!id || seen.has(id)) throw new Error(`team page ${page}: member ${id || "(no id)"} is missing or repeated`);
      seen.add(id); members.push(m);
    }
    if (!pg.hasMore) {
      if (members.length !== total) throw new Error(`team read: ${members.length} members read, ${total} announced`);
      return { members, roleDefinitions };
    }
    if (rows.length === 0) throw new Error(`team page ${page}: empty page while hasMore is true`);
  }
  throw new Error(`team read: more than ${TEAM_MAX_PAGES} pages`);
}
export function hydrateMembers() {
  if (_membersFetched) return;
  _membersFetched = true;
  if (typeof listTeamMembers !== "function") return;
  const gen = _membersGen;
  listAllTeamMembers(() => gen === _membersGen)
    .then((r: unknown) => {
      if (gen !== _membersGen) return; // the session ended while this read was in flight
      const raw = r as Record<string, any>;
      MEMBERS_ERROR = null;
      const roleRows: any[] = Array.isArray(raw.roleDefinitions) ? raw.roleDefinitions : [];
      if (roleRows.length) {
        API_ROLES = roleRows.map((r0) => ({
          key: String(r0.key ?? r0.id ?? ""), label: String(r0.label ?? r0.key ?? ""),
          description: String(r0.description ?? ""), isCustom: !!r0.isCustom,
          capabilities: Array.isArray(r0.capabilities) ? r0.capabilities : [],
          permissions: Array.isArray(r0.permissions) ? r0.permissions : [],
          memberCount: typeof r0.memberCount === "number" ? r0.memberCount : 0,
        }));
      }
      const rows: any[] = raw.members;
      if (rows.length) { MEMBERS = rows.map(toMemberFromApi); }
      if (roleRows.length || rows.length) notify();
    })
    .catch((e: unknown) => { if (gen !== _membersGen) return; MEMBERS_ERROR = "Couldn't load the team roster."; console.warn("listTeamMembers failed — roster unavailable:", e); notify(); });
}
/** Re-read the roster from the API (after a write). */
export function refetchMembers(): Promise<void> {
  _membersFetched = false;
  hydrateMembers();
  return Promise.resolve();
}
// Team roster + role-permission leak fix (docket 2026-07-26 & 2026-07-27):
// MEMBERS/API_ROLES/ROLE_STORE/CUSTOM_ROLES were fetched/persisted once with
// no tenant keying and no confirmation gate — purge SYNCHRONOUSLY the instant
// a switch starts (never show tenant A's roster/role-customisations under
// tenant B's chrome); reload only once the tenant is CONFIRMED (a request
// fired mid-switch would still run under the outgoing tenant's session
// against a real backend). `_membersFetched = true` during the pending
// window actively BLOCKS any incidental hydrateMembers() call in that gap
// (mirrors the guard hydrateMembers() already checks).
onTenantSwitching(() => { MEMBERS = []; API_ROLES = []; _membersFetched = true; ROLE_STORE = seedRoleStore(); CUSTOM_ROLES = {}; notify(); });
// Capability gate (docket 2026-07-31, persona-inappropriate prefetch): this is
// a module-level registration in the shell's own auth module, so it ran for
// every signed-in session regardless of role — the admin/team/members 403. The
// flag still resets unconditionally, and hydrateMembers() stays exported and
// ungated, so SettingsTeam (route-guarded on team.read) and refetchMembers()
// after a write both still work on demand.
onTenantChange(() => { _membersFetched = false; if (can("team.read")) hydrateMembers(); loadRoleStoreForTenant(getActiveTenantSlug()); notify(); });

export function listMembers(): Member[] { return MEMBERS; }
export function getMember(id: string): Member | undefined { return MEMBERS.find((m) => m.id === id); }

/* ── Member writes ─ real ops, awaited, reconciled from the response ────
 * Each throws on failure so the caller can render an honest error state;
 * nothing commits locally unless the server accepted the write. */

/** Change a member's tenant role (PATCH /admin/team/members/{id}). */
export async function setMemberRoleOp(id: string, roleKey: string): Promise<Member> {
  const res = await updateTeamMemberRole(id, { role: roleKey }, TEAM_OPTS) as unknown as Record<string, any>;
  const saved = toMemberFromApi(res.member ?? {});
  MEMBERS = MEMBERS.map((m) => (m.id === id ? saved : m));
  notify();
  return saved;
}

/** Invite a teammate (POST /admin/team/members). `capabilities`, when given,
 *  may only NARROW the role's set — the API rejects grants above the role. */
export async function inviteMemberOp(input: { email: string; roleKey: string; displayName?: string; inviteNote?: string; capabilities?: string[] }): Promise<Member> {
  const body: Record<string, unknown> = { email: input.email.trim(), role: input.roleKey };
  if (input.displayName?.trim()) body.displayName = input.displayName.trim();
  if (input.inviteNote?.trim()) body.inviteNote = input.inviteNote.trim();
  if (input.capabilities) body.capabilities = input.capabilities;
  const res = await inviteTeamMember(body as never, TEAM_OPTS) as unknown as Record<string, any>;
  const saved = toMemberFromApi(res.member ?? {});
  MEMBERS = [saved, ...MEMBERS];
  notify();
  return saved;
}

/** Resend a pending invite (POST /admin/team/members/{id}/resend). */
export async function resendInviteOp(id: string): Promise<void> {
  const res = await resendTeamInvite(id, TEAM_OPTS) as unknown as Record<string, any>;
  if (res?.member) {
    const saved = toMemberFromApi(res.member);
    MEMBERS = MEMBERS.map((m) => (m.id === id ? saved : m));
    notify();
  }
}

/** Revoke a membership (DELETE /admin/team/members/{id}). */
export async function removeMemberOp(id: string): Promise<void> {
  await removeTeamMemberOp(id, TEAM_OPTS);
  MEMBERS = MEMBERS.filter((m) => m.id !== id);
  notify();
}
export function subscribeMembers(fn: () => void) { return subscribe(fn); }

/** Effective capabilities for a member. Resolves against the per-process
 *  `assignments` when the row carries them (persona-sourced rows do); else the
 *  events-process fallback — the tenant role label is a display string, not a
 *  `can()` key. */
export function memberEffective(m: Member): Set<string> {
  return effectivePerms({ role: m.processRole || m.role, assignments: m.assignments ?? undefined, grants: m.grants, revokes: m.revokes });
}
/** The ceiling a member can be granted within — the union of their assigned
 *  roles' default sets across processes. */
export function memberCeiling(m: Member): string[] {
  if (m.assignments && Object.keys(m.assignments).length) {
    const set = new Set<string>();
    (Object.entries(m.assignments) as [ProcessId, string][]).forEach(([proc, role]) => {
      getRolePermsLive(proc, role).forEach((p) => set.add(p));
    });
    return [...set];
  }
  return getRolePermsLive("events", m.processRole || m.role);
}

/* ─── Sign-in personas + auth state ────────────────────────────────────
 * The mobile/tablet login is a persona chooser: pick a role to sign in as.
 * (The preview bundles against a stubbed backend; the real `login` client op
 * takes over server-side with no UI change.) Each persona maps to one of the
 * built-in events roles so the launcher + Event Day reflect real capabilities. */
export interface Persona {
  id: string; name: string; email: string; role: string;
  init: string; color: string; blurb: string;
  /** The persona's photo from /demo/personas (CC, 2026-10-05), https or same-origin only; null when absent. */
  photoUrl?: string | null;
}

// Stable display hue per persona id (UI-only presentation, not identity data). // ui-config
const PERSONA_HUES: Record<string, string> = {
  "p-coordinator": "#C77F14", "p-manager": "#0E7490", "p-creator": "#2D60EB",
  "p-approver": "#027D47", "p-admin": "#5142C7",
};
function toPersona(d: Record<string, any>): Persona {
  const name = d.displayName ?? d.name ?? d.email ?? "";
  return {
    id: d.id, name, email: d.email ?? "", role: d.role ?? "",
    init: initialsOf(name), color: PERSONA_HUES[d.id] ?? colorFor(d.id ?? name),
    blurb: d.blurb ?? "", photoUrl: safeImageUrl(d.photoUrl),
  };
}
/** Fetch the demo persona chooser options (getDemoPersonas → GET /demo/personas). */
export async function fetchPersonas(): Promise<Persona[]> {
  if (typeof getDemoPersonas !== "function") return [];
  try {
    const list = await getDemoPersonas();
    return (Array.isArray(list) ? list : []).map(toPersona);
  } catch (e) { console.warn("getDemoPersonas failed:", e); return []; }
}

let SIGNED_IN = false;
export function isSignedIn(): boolean { return SIGNED_IN; }

/* ── Landing resolution (2026-09-17 sign-in sequencing) ─────────────────────
 * `signIn()` flips SIGNED_IN the moment whoami answers, but the served landing
 * route is only known after the tenant composition has been fetched and
 * `nav.reset()` has fired — on a real tenant that gap was 3-7 s, and AuthGate
 * spent it rendering <AppRouter/> at the boot-time default `dashboard`: ~20
 * admin fetches a Team Member cannot call, two permission banners, a run of
 * console 403s, and on phone the launcher's "No apps are enabled" flash.
 *
 * RESOLVING_LANDING is set when sign-in begins and cleared by the caller ONLY
 * after the landing route has been applied (or the attempt has failed — the
 * clear sits in a `finally`, so a thrown profile fetch can never strand the
 * user on the loading state). AuthGate keeps <Signin/> mounted while it is
 * set. Host SSO (`setAuthenticated`) never sets it: that path has no landing
 * resolution of its own. */
let RESOLVING_LANDING = false;
export function isResolvingLanding(): boolean { return RESOLVING_LANDING; }
/** Caller has applied the landing route (or given up). Idempotent. */
export function landingResolved(): void {
  if (!RESOLVING_LANDING) return;
  RESOLVING_LANDING = false;
  notify();
}

function applyWhoamiUser(w: Record<string, any>) {
  const u = w?.user ?? w?.session?.user ?? {};
  const role = u.role ?? "";
  // Per-process assignments (workforce spec 2026-07-16): whoami may return
  // user.assignments = { events?: role, workforce?: role, … }. Falls back to
  // the single-role shape.
  const rawAssign = (u.assignments && typeof u.assignments === "object") ? u.assignments as Record<string, string> : null;
  const assignments: Partial<Record<ProcessId, string>> = {};
  if (rawAssign) {
    (Object.entries(rawAssign) as [ProcessId, string][]).forEach(([proc, r]) => {
      if (PROCESSES.some((p) => p.id === proc) && typeof r === "string" && r) assignments[proc] = r;
    });
  }
  if (!Object.keys(assignments).length && role) assignments.events = role;
  CURRENT_USER = {
    id: u.id ?? "", name: u.name ?? "", email: u.email ?? "", role,
    workspaceRole: Object.prototype.hasOwnProperty.call(w?.tenant ?? {}, "role")
      ? (typeof w.tenant.role === "string" ? w.tenant.role : null)
      : (typeof w?.role === "string" ? w.role : null),
    init: initialsOf(u.name ?? u.email ?? ""), color: colorFor(u.email ?? u.id ?? ""),
    assignments,
    grants: [], revokes: [],
    permissions: Array.isArray(u.permissions) && u.permissions.length ? u.permissions : null,
  };
}

/** Demo sign-in: mint a real role-scoped session for `personaId`, then hydrate
 *  the current user from whoami() — no baked identity. (#655) */
export async function signIn(personaId: string, photoUrl?: string | null): Promise<void> {
  _supersedeIdentityAttempt(); // sign-in hydrates its own identity below; an earlier retry's reply is obsolete
  const identityGen = _identityGen; // owned until a retry, session end, switch or newer sign-in moves it
  IMPERSONATION = null;
  RESOLVING_LANDING = true; // cleared by the caller via landingResolved()
  setSignedInPersonaId(personaId, photoUrl);
  if (typeof demoSignIn === "function") {
    /* A failed demo sign-in is a failed sign-in (CC 0210): undo what was set above,
       never mark the client signed in, and hand the error to the caller (the chooser
       and the deep-link path both catch it and stay on sign-in). */
    try { await demoSignIn(personaId); }
    catch (e) {
      setSignedInPersonaId(null);
      RESOLVING_LANDING = false;
      // Entry superseded the page's unresolved lookup and this sign-in will not
      // replace it, so ask once more, only if nothing newer has taken identity
      // over since. A recorded failed/tenantless result is kept.
      if (identityGen === _identityGen && !TENANT && TENANT_STATUS === "pending") { _tenantFetched = false; fetchTenant(); }
      notify();
      throw e;
    }
  }
  let hydrated = false;
  if (typeof whoami === "function") {
    try {
      const w = await whoami({ baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never) as Record<string, any>;
      if (w && (w.user ?? w.session?.user)) { applyWhoamiUser(w); hydrated = true; }
      // 2026-07-29: the new session's TENANT/org must be applied here too.
      // `fetchTenant` is one-shot (`_tenantFetched`), so without this the shell
      // kept whatever tenant the PAGE resolved at first load — sign in as a bank
      // persona after the page had resolved another org and you got that org's
      // chrome and its nav composition (no matching profile → generic rail).
      // Tenant-scoped caches are purged, then told to refetch.
      _tenantFetched = true;
      // CD docket 2026-09-17 (sign-in admin-surface fetch burst): a tenant
      // CHANGE means the incoming slug differs from the resident one — not
      // merely that whoami carried a tenant object (it always does). Firing
      // _fireTenantChange() on every sign-in woke every onTenantChange store
      // before RESOLVING_LANDING cleared or the role was known: 18 admin-scope
      // GETs, all 403 for a Team Member. Active screens hydrate through their
      // own hooks once the router mounts; a same-tenant sign-in needs no burst.
      const priorSlug = TENANT?.slug ?? null;
      _supersedeIdentityAttempt(); // a retry started while sign-in awaited cannot land after this
      const hadTenant = applyTenantIdentity(w);
      if (!hadTenant && !TENANT) TENANT_STATUS = "tenantless"; // r3: status only; sign-in outcome unchanged
      applyActiveOrganization(w);
      const tenantChanged = hadTenant && priorSlug !== null && TENANT!.slug !== priorSlug;
      if (tenantChanged) _fireTenantSwitching();
      SIGNED_IN = true; // stores gate their fetch on isSignedIn()
      if (tenantChanged) _fireTenantChange();
      // First resolution (null → slug) is not a change — same narrow signal
      // fetchTenant() uses; nothing hydrates on it.
      else if (hadTenant && priorSlug === null) _fireTenantIdentityResolved();
    } catch (e) { if (!TENANT) TENANT_STATUS = "failed"; console.warn("whoami after demoSignIn failed:", e); }
  }
  if (!hydrated) { CURRENT_USER = { id: personaId, assignments: {}, grants: [], revokes: [] }; }
  SIGNED_IN = true;
  if (typeof setStudioSessionActive === "function") setStudioSessionActive(true);
  notify();
}

/* ─── Expired session (CC 0176) ─────────────────────────────────────────────
   The api-client raises one "session expired" signal; App.tsx calls
   expireSession() with the screen the person was on. The signed-in state is
   cleared (the header stops showing the old person), sign-in shows the notice,
   and after signing in again the person returns to that screen. No form state is
   kept. */
export interface SessionExpiry { route: string; params: Record<string, unknown>; unsavedChanges: boolean }
let SESSION_EXPIRY: SessionExpiry | null = null;
/* Re-sign-in over the screen (CC 0306). When the session ends while an unsaved-changes guard is active,
   the screen stays mounted with what was typed and sign-in opens in a dialog over it. SIGNED_IN stays
   true so the tree isn't torn down, but the client's session is marked inactive, so every request
   until then raises the same signal and returns here (one dialog, however many calls failed).
   - Same person signs in: the dialog closes and they can save.
   - Different person (CC 0182): the screen state is dropped (screenEpoch bumps, the tree remounts)
     and they land on their own landing.
   Known limit: a host sign-in that leaves the page (an identity-provider redirect) reloads the
   document, and in-memory state can't survive that. The demo chooser and any in-page sign-in can. */
export interface Reauth { route: string; params: Record<string, unknown>; priorUserId: string }
let REAUTH: Reauth | null = null;
let SCREEN_EPOCH = 0;
export function getReauth(): Reauth | null { return REAUTH; }
/** Called by sign-in once the new session is hydrated. Says whether the same person came back. */
export function finishReauth(): "same" | "different" | null {
  const r = REAUTH; if (!r) return null;
  REAUTH = null;
  const same = !!r.priorUserId && CURRENT_USER.id === r.priorUserId;
  if (!same) SCREEN_EPOCH += 1;
  notify();
  return same ? "same" : "different";
}
export function expireSession(where: SessionExpiry): void {
  if (REAUTH) return;
  if (where.unsavedChanges && SIGNED_IN && !IMPERSONATION) {
    if (typeof setStudioSessionActive === "function") setStudioSessionActive(false);
    REAUTH = { route: where.route, params: where.params, priorUserId: String(CURRENT_USER.id ?? "") };
    notify();
    return;
  }
  if (!SIGNED_IN && SESSION_EXPIRY) return;
  if (typeof setStudioSessionActive === "function") setStudioSessionActive(false);
  SESSION_EXPIRY = where;
  SIGNED_IN = false; IMPERSONATION = null; CURRENT_USER = {}; RESOLVING_LANDING = false;
  _fireSessionEnd();
  notify();
}
/** The notice sign-in shows, or null when sign-in was not reached by an expiry. */
export function getSessionExpiry(): SessionExpiry | null { return SESSION_EXPIRY; }
/** Where to land after signing in again; read once (clears the notice). */
export function takeSessionReturn(): { route: string; params: Record<string, unknown> } | null {
  const e = SESSION_EXPIRY; SESSION_EXPIRY = null;
  return e ? { route: e.route, params: e.params } : null;
}
/** Real sign-out (JAI-920, kit 174e98dd): `logout` POSTs /auth/logout on the
 *  identity worker and revokes the session server-side. Before it reached the
 *  api-client barrel this function only reset local state, so the session stayed
 *  live after "Sign out". The op is idempotent, so it is safe alongside the
 *  host wrapper's compensating watcher, and local state is cleared even if the
 *  call fails — never strand the user on a screen they can't leave. */
export async function signOut(): Promise<void> {
  setSignedInPersonaId(null);
  try { await logout({ baseUrl: "", surface: "studio" }); }
  catch (e) { console.warn("logout failed — clearing local session anyway:", e); }
  SIGNED_IN = false; IMPERSONATION = null; CURRENT_USER = {}; RESOLVING_LANDING = false;
  SESSION_EXPIRY = null; REAUTH = null;
  if (typeof setStudioSessionActive === "function") setStudioSessionActive(false);
  _fireSessionEnd();
  notify();
}

/** Host-driven real-SSO bypass (packet #3 §3). The outer shell resolves its own
 *  authenticated identity (real whoami) and calls this once, before/at mount, to
 *  mark the session authenticated and hydrate the current user — skipping the demo
 *  persona chooser entirely. Nothing here touches demo-persona state. */
export function setAuthenticated(user: CurrentUser): void {
  IMPERSONATION = null;
  CURRENT_USER = {
    ...user,
    assignments: user.assignments ?? (user.role ? { events: user.role } : {}),
    grants: user.grants ?? [],
    revokes: user.revokes ?? [],
  };
  SIGNED_IN = true;
  if (typeof setStudioSessionActive === "function") setStudioSessionActive(true);
  notify();
}
/** Re-renders on sign-in/out (and any access change). */
export function useAuth(): { signedIn: boolean; user: CurrentUser; resolvingLanding: boolean; reauth: Reauth | null; screenEpoch: number } {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return { signedIn: SIGNED_IN, user: CURRENT_USER, resolvingLanding: RESOLVING_LANDING, reauth: REAUTH, screenEpoch: SCREEN_EPOCH };
}

/* ─── Impersonation ─ admin "view as" another member ───────────────────── */
export interface Impersonation {
  realUser: CurrentUser;
  target: CurrentUser;
  /** When this acting-as session began — the banner shows how long it's run. */
  startedAt: number;
}
let IMPERSONATION: Impersonation | null = null;
export function getImpersonation(): Impersonation | null { return IMPERSONATION; }
/** True while the signed-in admin is acting as someone else. */
export function isImpersonating(): boolean { return IMPERSONATION !== null; }
/** The human actually at the keyboard — the admin, even mid-impersonation.
 *  Anything that ATTRIBUTES an action (audit, logs) must read this, not
 *  `getUser()`, which deliberately returns the person being acted as.
 *  NB: this module must not import lib/audit.ts (audit.ts imports this file);
 *  start/stop entries are written by the call site and the banner. */
export function getRealUser(): CurrentUser { return IMPERSONATION ? IMPERSONATION.realUser : CURRENT_USER; }
/** Re-renders whenever acting-as starts or stops. */
export function useImpersonation(): Impersonation | null {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return IMPERSONATION;
}

export function startImpersonation(member: { id?: string; name?: string; email?: string; role?: string; init?: string; color?: string; assignments?: Partial<Record<ProcessId, string>> | null; grants?: string[]; revokes?: string[]; locations?: string[] | null }): boolean {
  if (!member) return false;
  /* A role OR per-process assignments is enough. Requiring `role` meant a
     person whose access lives entirely in `assignments` silently no-oped — the
     caller toasted "Signed in as …" and navigated while the admin was still
     themselves. Callers get a boolean now and can be honest about a refusal. */
  const hasAssignments = !!member.assignments && Object.keys(member.assignments).length > 0;
  if (!member.role && !hasAssignments) return false;
  const realUser = IMPERSONATION ? IMPERSONATION.realUser : CURRENT_USER;
  // Persona-sourced members carry per-process assignments — impersonating the
  // branch manager must resolve her WORKFORCE role, not events-wrap her label.
  const assignments: Partial<Record<ProcessId, string>> =
    hasAssignments ? (member.assignments as Partial<Record<ProcessId, string>>) : { events: member.role as string };
  const role = member.role || Object.values(assignments).find(Boolean) || "";
  const target: CurrentUser = {
    id: member.id, name: member.name || (member.email || "Teammate").split("@")[0],
    email: member.email || "", role, init: member.init || initialsOf(member.name || member.email || "?"),
    color: member.color || "#1F4FA8",
    assignments, grants: member.grants || [], revokes: member.revokes || [],
    permissions: [...effectivePerms({ role, assignments, grants: member.grants, revokes: member.revokes })],
    locations: member.locations || null,
  };
  IMPERSONATION = { realUser, target, startedAt: Date.now() };
  CURRENT_USER = target; notify();
  return true;
}
export function stopImpersonation() {
  if (!IMPERSONATION) return;
  CURRENT_USER = IMPERSONATION.realUser; IMPERSONATION = null; notify();
}
